import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { map, catchError, mergeMap, tap, finalize, filter, take, concatMap } from 'rxjs/operators';
import { from as observableFrom } from 'rxjs/observable/from';
import { forkJoin as ObservableForkJoin } from 'rxjs/observable/forkJoin';
import { _throw as ObservableThrow } from 'rxjs/observable/throw';
import { of as ObservableOf } from 'rxjs/observable/of';
import {
  isNil as _isNil,
  isEmpty as _isEmpty,
  forEach as _forEach,
  cloneDeep as _cloneDeep,
  orderBy as _orderBy,
  reject as _reject,
  find as _find,
  map as _map,
  some as _some,
  replace as _replace,
  startsWith as _startsWith,
  filter as _filter,
  concat as _concat,
  every as _every,
  assign as _assign,
  compact as _compact,
  trim as _trim,
  toString as _toString,
  defaultTo as _defaultTo,
  flatMap as _flatMap,
  uniq as _uniq,
  includes as _includes,
  sortBy as _sortBy,
  countBy as _countBy,
} from 'lodash';
import * as moment from 'moment';
import {
  DateTimeUtils,
  CookieUtils,
  Const,
  StringUtils,
  DateTimeOutput,
  DateTimePadding,
  DateSeparator,
  DateEra,
  LocalStorageUtils,
} from '@blcloud/bl-common';
import { CustomerConst } from '@blcloud/bl-datamodel/const/customer';
import { CompanyConst } from '@blcloud/bl-datamodel/const/company';
import { LogicalDeleteDiv } from '@blcloud/bl-datamodel/enum/common/logical-delete-div';
import { ContactDateSearchDiv } from '@blcloud/bl-datamodel/enum/deals/contact-date-search-div';
import { ContactExtractTargetMPExistDiv } from '@blcloud/bl-datamodel/enum/deals/contact-extract-target-m-p-exist-div';
import {
  ContactExtractTargetEntrustedContractExistDiv,
} from '@blcloud/bl-datamodel/enum/lease/contact-extract-target-entrusted-contract-exist-div';
import { ContactExtractDmSendDiv } from '@blcloud/bl-datamodel/enum/approach/contact-extract-dm-send-div';
import { DmSendDiv } from '@blcloud/bl-datamodel/enum/approach/dm-send-div';
import { CalculateDiv } from '@blcloud/bl-datamodel/enum/common/calculate-div';
import {
  ILabel,
  IOrganizationInformation,
  ITaskInfo,
  IContactHistory,
  IContactMultiplePartialUpdate,
  IContactInitDisplaySetting,
  IContactInitDisplaySettingDtl,
  IContactStatusBgColorSetting,
  IContactLostReason,
  IContactItem,
  IContactExecuteResult,
  IReportOutputResult,
  IGenericPrintInstruction,
  IContactListPrintInstruction,
  IContactSummary,
  IBulkUpdateProgress,
  IBulkProcessResult,
  IContactMultipleDelete,
  IVehicleInfo,
  IContact,
  IDraftContactListDisp,
  IContactListExtractProgress,
  VehicleInspCertifInfo,
  IVehicleItemDispNameSetting,
} from '@blcloud/bl-datamodel';
import { CodeDivGenre } from '@blcloud/bl-datamodel/enum/common/code-div-genre';
import { IContactUpdDelTargetChangeInfo } from '@blcloud/bl-datamodel/entity/deals/contact-upd-del-target-change-info';
import { UpdDelTargetOperationDiv } from '@blcloud/bl-datamodel/enum/deals/upd-del-target-operation-div';
import {
  OrganizationInformationResource,
  LabelResource,
  ExportConditionInfoResource,
  ContactListDispResource,
  ContactListExtractProgressResource,
  ContactResource,
  ContactInitDisplaySettingResource,
  ContactStatusBgColorSettingResource,
  ContactLostReasonResource,
  ContactItemResource,
  ContactExecuteResultResource,
  BulkProcessResultResource,
  VehicleInfoResource,
  VehicleItemDispNameSettingResource,
} from '@blcloud/bl-ng-resource';
import {
  BlApiErrorInfo,
  BlApiQueryParams,
  BlConditionInfo,
  BlConditionType,
  HttpStatus,
  NotificationPushService,
  WebsocketSubject,
  BlApiLocator,
  BlListResponseBody
} from '@blcloud/bl-ng-common';
import { BlDialogService, ModalReason } from '@blcloud/bl-ng-ui-component';
import { LoginResourceService, DivisionResourceService, FrontDivisionBody } from '@blcloud/bl-ng-share-module';
import {
  ContactSearchCondition,
  IContactListGridSetting,
  CONTACT_LIST_PAGE_SEARCH_PARAM_KEY,
  CONTACT_LIST_GRID_SETTING_KEY,
  SortOrderDiv,
  OrgMasterKind,
  IOrgMasterItem,
  ORG_CODE_PAIR_SEPARATOR,
  ORG_MASTER_NOT_FOUND_CODE,
  buildOrgMasterSelectionKey,
  normalizeOrgMasterSelectionKey,
} from './contact-list-page.interface';
import { IContactInitDisplaySettingEx } from '../../feature/contact-init-setting/contact-init-setting-modal.interface';
import { of as RxOf } from 'rxjs/observable/of';
import { timer as ObservableTimer } from 'rxjs/observable/timer';
import { Injector } from '@angular/core';
import { UUID } from 'angular2-uuid';
import { Subject } from 'rxjs/Subject';
import { BulkUpdateStatus } from '@blcloud/bl-datamodel/enum/bizcmn/bulk-update-status';
import { ContactCreateDiv } from '@blcloud/bl-datamodel/enum/deals/contact-create-div';
import { ContractConst } from '@blcloud/bl-datamodel/const/contract';
import { RoleConst } from '@blcloud/bl-datamodel/const/role';
import {
  BlApiErrorInfoWithContactListExtractDiv,
  ENTRUSTED_CONTRACT_EXIST_DIV_SEARCH,
  IContactEx,
  IContactForGrid,
  LEASE_CONTACT_EXECUTE_ITEM_CODE_LIST,
  PeriodDiv,
} from '../../shared/contact/contact-page.interface';
import { ContactListExtractProgressDiv } from '@blcloud/bl-datamodel/enum/deals/contact-list-extract-progress-div';

/**
 * コンタクト一覧ページサービス
 */
@Injectable()
export class ContactListPageService {

  /** バックエンドの下書き1件あたり最大件数（ContactListDispApiService.CONTACT_LIST_EXTRACT_DRAFT_BATCH_SIZE）と一致させること */
  private static readonly CONTACT_LIST_EXTRACT_BATCH_SIZE = 1000;

  /** コンタクト実施結果リストキャッシュ保持Map */
  private cacheContactExecuteResultListMap: Map<string, IContactExecuteResult[]> = new Map();

  /** コンタクト失注理由リストキャッシュ保持Map */
  private cacheContactLostReasonListMap: Map<string, IContactLostReason[]> = new Map();

  /** コンタクト内容リストキャッシュ保持Map */
  private cacheContactItemListMap: Map<string, IContactItem[]> = new Map();

  /**
   * 2列UI用マスタ候補キャッシュ（種別＋組織コード）。
   * registerSelectableOrganizations 時に全クリア。ドロップダウン開封はヒット時 API 再取得なし。
   */
  private cacheOrgMasterItemsMap: Map<string, IOrgMasterItem[]> = new Map();

  /** 組織情報 */
  private organizationInfoList: IOrganizationInformation[] = [];

  /** 組織マルチセレクトの候補（検索条件が空＝全組織のときのマスタ取得・名称表示用） */
  private selectableOrganizationCodes: string[] = [];

  /** 現在セッションのテナントID（テナント切替時にマスタキャッシュをリセットする） */
  private activeBlTenantId: string | null = null;

  /** ラベルマスタ情報 */
  private LabelList: ILabel[] = [];

  /** 受託リース契約機能有効フラグ */
  _isAvailableEntrustedLease = false;

  /** 自動車追加情報区分（vcleAddInfo1 の名称解決用） */
  private vcleAdditionalInfoCdList: FrontDivisionBody[] = [];

  /** 一覧抽出のクライアントセッションID */
  private clientSessionId: string;

  /** 一覧抽出の現在ページ（0始まり、グリッドのページ番号） */
  private extractCurrentPageNumber = 0;

  /** 一覧抽出時のグリッド1ページあたり件数（バックエンドの下書きバッチ件数と揃えたインデックス計算に使用） */
  private extractCurrentPageSize = 50;

  /** 直近の抽出完了時の検索結果総件数（進捗 totalCount） */
  lastExtractTotalCount = 0;

  /** 直近の抽出完了時の業務セッションID一覧 */
  lastBusinessSessionIdList: string[] = [];

  /** 一括処理状況（BulkProcessResult.bulkProcessTargetKey から表示名へ）。新規抽出でクリア。 */
  private bulkUpdateResultStatusByTargetKey = new Map<string, string>();

  /** WebSocket接続 */
  protected webSocketSubject: WebsocketSubject<{ message: string, uniqueId?: string }>;

  /** ロケータ */
  protected blApiLocator: BlApiLocator;

  /** WebSocket */
  private WEB_SOCKET: { DOMAIN: string, PROTOCOL: string, ENDPOINT: string } = {
    /** ドメイン */
    DOMAIN: 'notification',

    /** プロトコル */
    PROTOCOL: 'wss:',

    /** エンドポイント */
    ENDPOINT: '/notificationoutsideappmessage',
  };

  /** 一括更新WebSocketID */
  protected bulkUpdateWebSocketId = UUID.UUID();

  /** 一括更新管理ID */
  protected bulkUpdateManageId: string;

  /** 一括更新進捗状況通知 */
  protected bulkUpdateProgressSubject = new Subject<IBulkUpdateProgress>();

  /** ネットワーク接続エラー */
  private ERR_NETWORK = 'インターネット接続に失敗しました。\nネットワーク接続を確認して下さい。';

  /** 編集内容破棄 */
  INFO_EDITING = '#EXEC_CNT# / #TOTAL_CNT# 件更新完了\nしばらくお待ちください。\n'
    + '<span class="bulk-edit-loading-description">※画面を閉じても処理は継続されますが、結果の確認ができなくなります</span>';

  /** 置換文字 */
  REPLACE: { EXEC_CNT: string, TOTAL_CNT: string, LABEL_CNT: string } = {
    /** 処理件数 */
    EXEC_CNT: '#EXEC_CNT#',

    /** 総件数 */
    TOTAL_CNT: '#TOTAL_CNT#',

    /** ラベル件数 */
    LABEL_CNT: '#LABEL_CNT#',
  };

  /** 一括処理結果区分 */
  private BULK_PRO_RES_DIV_ENUM_JAP_NAME: { SUCCESS: string, FAILURE: string } = {
    SUCCESS: '処理成功',
    FAILURE: '処理失敗'
  };

  /**
   * コンストラクタ
   */
  constructor(
    private contactListDispResource: ContactListDispResource,
    private contactListExtractProgressResource: ContactListExtractProgressResource,
    private organizationInformationResource: OrganizationInformationResource,
    private labelResource: LabelResource,
    private exportConditionInfoResource: ExportConditionInfoResource,
    private contactResource: ContactResource,
    private contactInitDisplaySettingResource: ContactInitDisplaySettingResource,
    private contactStatusBgColorSettingResource: ContactStatusBgColorSettingResource,
    private contactLostReasonResource: ContactLostReasonResource,
    private contactItemResource: ContactItemResource,
    private contactExecuteResultResource: ContactExecuteResultResource,
    private bulkProcessResultResource: BulkProcessResultResource,
    private vehicleInfo: VehicleInfoResource,
    private vehicleItemDispNameSettingResource: VehicleItemDispNameSettingResource,
    private loginResourceService: LoginResourceService,
    private divisionService: DivisionResourceService,
    private dialogService: BlDialogService,
    protected injector: Injector
  ) {
    this.blApiLocator = injector.get(BlApiLocator);
  }

  /**
   * 車両項目表示名称設定を取得します。取得失敗時は空オブジェクトを返します。
   */
  getVehicleItemDispNameSetting(): Observable<IVehicleItemDispNameSetting> {
    return this.vehicleItemDispNameSettingResource.get<IVehicleItemDispNameSetting>().pipe(
      catchError(() => ObservableOf({} as IVehicleItemDispNameSetting))
    );
  }

  /**
   * コンタクト一覧検索条件をローカルストレージに保存します。
   * @param searchParam コンタクト一覧検索条件
   */
  saveSearchParam(searchParam: ContactSearchCondition): void {
    const toSave = _assign({}, searchParam, {
      savedBlTenantId: this.activeBlTenantId || searchParam.savedBlTenantId,
    });
    LocalStorageUtils.save(CONTACT_LIST_PAGE_SEARCH_PARAM_KEY, toSave);
  }

  /**
   * コンタクト一覧グリッド設定を取得します。
   * @returns グリッド設定
   */
  getGridSetting(): IContactListGridSetting {
    // ローカルストレージからグリッド設定を取得
    const gridSetting = LocalStorageUtils.get(CONTACT_LIST_GRID_SETTING_KEY) as IContactListGridSetting;
    // 未保存の場合は空の設定を返し、グリッド側の初期値に委ねる
    return !_isNil(gridSetting) ? gridSetting : {};
  }

  /**
   * コンタクト一覧グリッド設定を保存します。
   * @param gridSetting グリッド設定
   */
  saveGridSetting(gridSetting: IContactListGridSetting): void {
    // 次回表示時に復元できるようローカルストレージへ保存
    LocalStorageUtils.save(CONTACT_LIST_GRID_SETTING_KEY, gridSetting);
  }

  /**
   * ログインテナントが変わったときに組織マスタ関連の状態をリセットします。
   * 他テナントにログインするとローカルストレージにより「[] 他社受注」が表示されるため、
   * テナント切替時はマスタキャッシュ・組織名称をクリアし、2列UI選択キーと検索Inだけ当該テナント用に初期化する（期間・組織・その他の検索条件はローカルストレージに残す）。
   * @param blTenantId テナントID
   */
  ensureTenantContext(blTenantId: string): void {
    const tenantId = _trim(_toString(blTenantId));
    if (_isEmpty(tenantId)) {
      return;
    }
    if (this.activeBlTenantId !== tenantId) {
      const tenantSwitched = !_isEmpty(this.activeBlTenantId);
      this.activeBlTenantId = tenantId;
      this.cacheOrgMasterItemsMap.clear();
      this.cacheContactLostReasonListMap.clear();
      this.cacheContactItemListMap.clear();
      this.organizationInfoList = [];
      this.selectableOrganizationCodes = [];
      if (tenantSwitched) {
        const saved = LocalStorageUtils.get(CONTACT_LIST_PAGE_SEARCH_PARAM_KEY) as ContactSearchCondition | null;
        if (!_isNil(saved)) {
          LocalStorageUtils.save(CONTACT_LIST_PAGE_SEARCH_PARAM_KEY, _assign({}, saved, {
            contactLostReasonOrgMasterKeys: [],
            contactItemOrgMasterKeys: [],
            contactLostReasonCode: [],
            contactItemCode: [],
            savedBlTenantId: tenantId,
          }));
        }
      }
    }
  }

  /**
   * 保存済のコンタクト一覧検索条件を取得して返します。
   * 他テナントにログインするとローカルストレージにより「[] 他社受注」が表示されるため、
   * savedBlTenantId がログイン中テナントと一致しない場合は復元せず、未設定の旧 LS は2列UIキーと In をクリアする。
   * @param expectedBlTenantId 現在ログイン中のテナントID（不一致時は復元しない）
   * @returns コンタクト一覧検索条件。テナント不一致時は undefined
   */
  getSavedSearchParam(expectedBlTenantId?: string): ContactSearchCondition | undefined {
    const searchParam = LocalStorageUtils.get(CONTACT_LIST_PAGE_SEARCH_PARAM_KEY) as ContactSearchCondition & {
      contactLostReasonCodes?: string[];
      contactItemCodes?: string[];
      savedBlTenantId?: string;
    };
    let persistLegacyTenantMigration = false;
    if (!_isNil(searchParam) && !_isEmpty(expectedBlTenantId)) {
      const tenantId = _trim(_toString(expectedBlTenantId));
      if (!_isEmpty(searchParam.savedBlTenantId) && searchParam.savedBlTenantId !== tenantId) {
        return undefined;
      }
      if (_isEmpty(searchParam.savedBlTenantId)) {
        searchParam.contactLostReasonOrgMasterKeys = [];
        searchParam.contactItemOrgMasterKeys = [];
        searchParam.contactLostReasonCode = [];
        searchParam.contactItemCode = [];
        searchParam.savedBlTenantId = tenantId;
        persistLegacyTenantMigration = true;
      }
    }
    if (!_isNil(searchParam)) {
      // ローカルストレージの旧フィールド名（複数形）を現行の In 用配列へ寄せる（2列UI 導入前の保存データ向け）
      if (_isEmpty(searchParam.contactLostReasonCode) && !_isEmpty(searchParam.contactLostReasonCodes)) {
        searchParam.contactLostReasonCode = searchParam.contactLostReasonCodes;
      }
      if (_isEmpty(searchParam.contactItemCode) && !_isEmpty(searchParam.contactItemCodes)) {
        searchParam.contactItemCode = searchParam.contactItemCodes;
      }
      // 旧UI（数値・単一値）で保存された失注理由／コンタクト区分を In の文字列配列へ正規化
      searchParam.contactLostReasonCode = this.normalizeOrgMasterInCodes(searchParam.contactLostReasonCode);
      searchParam.contactItemCode = this.normalizeOrgMasterInCodes(searchParam.contactItemCode);
      searchParam.entrustedContractExistDivSearch = this.normalizeEntrustedContractExistDivSearchForListRadio(
        searchParam.entrustedContractExistDivSearch,
      );
      searchParam.contactLostReasonOrgMasterKeys = _map(
        searchParam.contactLostReasonOrgMasterKeys || [],
        key => normalizeOrgMasterSelectionKey(key),
      );
      searchParam.contactItemOrgMasterKeys = _map(
        searchParam.contactItemOrgMasterKeys || [],
        key => normalizeOrgMasterSelectionKey(key),
      );
    }
    if (!_isNil(searchParam) && !_isEmpty(searchParam.periodDiv)) {
      // 対象期間は相対的な値なので更新
      _assign(searchParam, this.getContactSearchDateRange(searchParam.periodDiv));
    }
    if (persistLegacyTenantMigration && !_isNil(searchParam)) {
      this.saveSearchParam(searchParam);
    }
    return searchParam;
  }

  /**
   * 指定組織コードが有効であるか判定します。
   * @param organizationCode 組織コード
   * @returns 判定結果
   */
  isExistOrganization(organizationCode: string): Observable<boolean> {
    if (_isEmpty(organizationCode)) {
      return ObservableOf(false);
    }
    const today = DateTimeUtils.today();
    return this.organizationInformationResource.getAll<IOrganizationInformation>({
      conditions: [
        {
          key: 'organizationCode',
          value: organizationCode,
          type: BlConditionType.Equal,
        },
        {
          key: 'applyStartDate',
          value: today,
          type: BlConditionType.LessThanEqual,
        },
        {
          key: 'applyEndDate',
          value: today,
          type: BlConditionType.GreaterThanEqual,
        },
        {
          key: 'logicalDeleteDiv',
          value: LogicalDeleteDiv.Valid,
          type: BlConditionType.Equal,
        }
      ],
      fields: ['organizationCode'],
    }, {
      consistent: false,
      entityOptions: { exPath: 'searchwithallcommonorg' }
    }).pipe(
      map(response => !_isEmpty(response.searchResultList)),
      catchError(() => ObservableOf(false))
    );
  }

  /**
   * 期間区分に一致する検索日時の期間を返します。
   * @param periodDiv 期間区分
   * @returns 開始・終了日時
   */
  private getContactSearchDateRange(periodDiv: string): Pick<ContactSearchCondition, 'contactSearchDateStart' | 'contactSearchDateEnd'> {
    let contactSearchDateStart: string;
    let contactSearchDateEnd: string;
    switch (periodDiv) {
      case PeriodDiv.LastMonth:
        contactSearchDateStart = moment().subtract(1, 'M').clone().startOf('M').format(DateTimeUtils.formatString.date);
        contactSearchDateEnd = moment().subtract(1, 'M').clone().endOf('M').format(DateTimeUtils.formatString.date);
        break;
      case PeriodDiv.LastWeek:
        contactSearchDateStart = moment().subtract(1, 'weeks').startOf('week').format(DateTimeUtils.formatString.date);
        contactSearchDateEnd = moment().subtract(1, 'weeks').endOf('week').format(DateTimeUtils.formatString.date);
        break;
      case PeriodDiv.Yesterday:
        contactSearchDateStart = moment().subtract(1, 'days').format(DateTimeUtils.formatString.date);
        contactSearchDateEnd = moment().subtract(1, 'days').format(DateTimeUtils.formatString.date);
        break;
      case PeriodDiv.Today:
        contactSearchDateStart = DateTimeUtils.formatIso(DateTimeUtils.today());
        contactSearchDateEnd = DateTimeUtils.formatIso(DateTimeUtils.today());
        break;
      case PeriodDiv.Tomorrow:
        contactSearchDateStart = moment().add(1, 'days').format(DateTimeUtils.formatString.date);
        contactSearchDateEnd = moment().add(1, 'days').format(DateTimeUtils.formatString.date);
        break;
      case PeriodDiv.ThisWeek:
        contactSearchDateStart = moment().startOf('week').format(DateTimeUtils.formatString.date);
        contactSearchDateEnd = moment().endOf('week').format(DateTimeUtils.formatString.date);
        break;
      case PeriodDiv.NextWeek:
        contactSearchDateStart = moment().add(1, 'weeks').startOf('week').format(DateTimeUtils.formatString.date);
        contactSearchDateEnd = moment().add(1, 'weeks').endOf('week').format(DateTimeUtils.formatString.date);
        break;
      case PeriodDiv.ThisMonth:
        contactSearchDateStart = moment().startOf('M').format(DateTimeUtils.formatString.date);
        contactSearchDateEnd = moment().endOf('M').format(DateTimeUtils.formatString.date);
        break;
      case PeriodDiv.NextMonth:
        contactSearchDateStart = moment().add(1, 'M').clone().startOf('M').format(DateTimeUtils.formatString.date);
        contactSearchDateEnd = moment().add(1, 'M').clone().endOf('M').format(DateTimeUtils.formatString.date);
        break;
      default:
        return {};
    }
    return {
      contactSearchDateStart: contactSearchDateStart + 'T00:00:00',
      contactSearchDateEnd: contactSearchDateEnd + 'T23:59:59',
    };
  }

  /**
   * 自動車追加情報1表示用の区分マスタを読み込みます（画面起動時に1回呼び出す想定）。
   */
  loadVcleAdditionalInfoCdList(): Observable<void> {
    return this.divisionService.fetchTransformFrontDivision([
      CodeDivGenre.ContactEnableTime,
      CodeDivGenre.VcleAdditionalInfoCd,
    ]).pipe(
      tap(division => {
        this.vcleAdditionalInfoCdList = division[CodeDivGenre.VcleAdditionalInfoCd] || [];
      }),
      mergeMap(() => ObservableOf(undefined))
    );
  }

   /**
   * 終了処理を行います。
   */
  destroy(): void {
    this.bulkUpdateProgressSubject.complete();
    this.bulkUpdateResultStatusByTargetKey.clear();
    if (!_isNil(this.webSocketSubject)) {
      this.webSocketSubject.unsubscribe();
      this.webSocketSubject = null;
    }
  }

  /**
   * コンタクト初期表示設定を取得します。
   * @param employeeCode 従業員コード
   */
  getContactInitDisplaySetting(employeeCode: string): Observable<IContactInitDisplaySetting> {
    return this.contactInitDisplaySettingResource.getById<IContactInitDisplaySetting>(employeeCode).pipe(
      catchError((error: BlApiErrorInfo) => {
        if (error.status === HttpStatus.NotFound) {
          return ObservableOf(null);
        } else {
          return ObservableThrow(error);
        }
      })
    );
  }

  /**
   * 一覧向け初期表示設定詳細を検索条件へ反映し、組織別マスタの検索In／2列UI選択キーを設定します。
   * @param dtl 一覧向け初期表示設定詳細
   * @param condition 反映先のコンタクト一覧検索条件（本メソッド内で上書きされる）
   * @param options オプション契約状態（メンテパック・受託リースの有効フラグ）
   * @returns 失注理由・コンタクト内容の2列UI復元用キー
   */
  applyInitDisplayDtlGridToListPage(
    dtl: IContactInitDisplaySettingDtl,
    condition: ContactSearchCondition,
    options: { isAvailableMaintePack: boolean; isAvailableEntrustedLease: boolean },
  ): Observable<{ lostKeys: string[]; itemKeys: string[] }> {
    // 初期表示DBをローカルストレージより優先するため、一覧詳細の内容で上書き（空も明示反映）
    condition.contactDateSearchDiv = (!_isNil(dtl.contactDateSearchDiv) && dtl.contactDateSearchDiv !== '')
      ? dtl.contactDateSearchDiv
      : ContactDateSearchDiv.ContactScheduleDateTime;

    // 一覧検索組織: 組織コード配列が設定済みならその内容（空配列＝全組織）。未設定の旧データのみ代表組織コード1件へフォールバック
    condition.organizationCodes = this.normalizeInitDisplayOrganizationCodes(dtl);

    // 詳細検索の各項目を初期表示詳細の値で固定（未設定は空／未選択相当でローカルストレージの当該項目を消す）
    condition.customerLabelCodeList = this.asInitDisplayNumericCodeArray(dtl.customerLabelCodeList);
    condition.contactDivSettingCodeList = this.asInitDisplayNumericCodeArray(dtl.contactDivDetailCodeList);
    condition.customerNameKana1 = _isNil(dtl.customerNameKana1) ? '' : dtl.customerNameKana1;

    // コンタクト抽出対象DM案内可否（顧客）を反映
    const customerExtractDm = !_isNil(dtl.contactExtractCustomerDmSendDiv) && dtl.contactExtractCustomerDmSendDiv !== ''
      ? dtl.contactExtractCustomerDmSendDiv
      : ContactExtractDmSendDiv.Send;
    condition.customerDmSendDiv = customerExtractDm === ContactExtractDmSendDiv.All
      ? ''
      : this.mapContactExtractDmSendDivToSearchDmSendDiv(customerExtractDm);

    const vehicleExtractDm = !_isNil(dtl.contactExtractVehicleDmSendDiv) && dtl.contactExtractVehicleDmSendDiv !== ''
      ? dtl.contactExtractVehicleDmSendDiv
      : ContactExtractDmSendDiv.Send;
    condition.vehicleDmSendDiv = vehicleExtractDm === ContactExtractDmSendDiv.All
      ? ''
      : this.mapContactExtractDmSendDivToSearchDmSendDiv(vehicleExtractDm);

    // MP オプション契約に応じて検索条件の MP 有無を dtl から決定
    if (options.isAvailableMaintePack) {
      const mpDiv = dtl.contactExtractMPExistDiv;
      condition.contactExtractMPExistDiv = (!_isNil(mpDiv) && mpDiv !== ContactExtractTargetMPExistDiv.None)
        ? mpDiv
        : ContactExtractTargetMPExistDiv.None;
    } else {
      condition.contactExtractMPExistDiv = ContactExtractTargetMPExistDiv.None;
    }

    // コンタクト抽出対象リース受託契約有無区分を、一覧検索の受託リース契約有無区分（検索用）へ反映
    if (options.isAvailableEntrustedLease) {
      const extractLeaseDiv = this.normalizeContactExtractEntrustedContractExistDivForRadio(
        dtl.contactExtractEntrustedContractExistDiv,
        ContactExtractTargetEntrustedContractExistDiv.All,
      );
      condition.entrustedContractExistDivSearch = this.normalizeEntrustedContractExistDivSearchForListRadio(
        this.mapContactExtractEntrustedContractExistDivToSearch(extractLeaseDiv),
      );
    } else {
      condition.entrustedContractExistDivSearch = ENTRUSTED_CONTRACT_EXIST_DIV_SEARCH.NONE;
    }

    // 抽出結果の並び順も未設定時はデフォルト（顧客名称カナ）へ揃える
    condition.sortOrderDiv = (!_isNil(dtl.contactExtractResultSortOrderDiv) && dtl.contactExtractResultSortOrderDiv !== '')
      ? dtl.contactExtractResultSortOrderDiv
      : SortOrderDiv.CustomerNameKana;

    const orgCodes = condition.organizationCodes;
    const lostInFromExtract = this.normalizeContactExtractOrgMasterInCodes(dtl.contactExtractLostReasonCodeList);
    const itemInFromExtract = this.normalizeContactExtractOrgMasterInCodes(dtl.contacExtractItemCodeList);
    const lostKeys = this.orgMasterInCodesToUiKeys(lostInFromExtract, orgCodes);
    const itemKeys = this.orgMasterInCodesToUiKeys(itemInFromExtract, orgCodes);
    condition.contactLostReasonCode = lostInFromExtract;
    condition.contactItemCode = itemInFromExtract;
    return ObservableOf({ lostKeys, itemKeys });
  }

  /** 検索In（組織コード_コード形式）を2列UI選択キーへ変換します */
  orgMasterInCodesToUiKeys(inCodes: string[], organizationCodes?: string[]): string[] {
    // 組織コード配列を正規化
    const orgCodes = _compact(_map(organizationCodes || [], code => _trim(_toString(code))));
    // 検索In値を正規化
    return _uniq(_compact(_map(this.normalizeOrgMasterInCodes(inCodes), value => {
      const trimmed = normalizeOrgMasterSelectionKey(value);
      if (_isEmpty(trimmed)) {
        return null;
      }
      // 組織コード_コードの区切りを取得
      const sepIndex = trimmed.lastIndexOf(ORG_CODE_PAIR_SEPARATOR);
      if (sepIndex > 0 && sepIndex < trimmed.length - 1) {
        return trimmed;
      }
      // 組織コード配列が1件のときは組織コード_コードに変換
      if (orgCodes.length === 1) {
        return buildOrgMasterSelectionKey(orgCodes[0], trimmed);
      }
      return null;
    })));
  }

  /**
   * 一覧向け初期表示設定を保存用に整形します（2列UI選択キーからコンタクト抽出対象コード配列へ変換）。
   * @param dtl 一覧向け初期表示設定詳細
   * @param lostReasonKeys 失注理由の2列UI選択キー（組織コード_コード）
   * @param contactItemKeys コンタクト内容の2列UI選択キー（組織コード_コード）
   * @param selectedOrganizationCodes 一覧欄で選択した組織コード
   * @param fallbackOrganizationCode 一覧組織未選択（全組織検索）時にAPI必須の代表組織コードへ補填する値（例: ログイン従業員の組織）
   * @returns 保存形式へ変換済みの初期表示設定詳細
   */
  resolveInitDisplayGridForSave(
    dtl: IContactInitDisplaySettingDtl,
    lostReasonKeys: string[],
    contactItemKeys: string[],
    selectedOrganizationCodes: string[],
    fallbackOrganizationCode?: string,
  ): Observable<IContactInitDisplaySettingDtl> {
    // 旧レコード（一覧組織コード配列が未設定）のみ、代表組織コード1件から組織コード配列を復元
    if (_isNil(dtl.organizationCodeList) && !_isEmpty(dtl.organizationCode)) {
      dtl.organizationCodeList = [dtl.organizationCode];
    }
    // 組織コード配列を設定
    const orgCodes = selectedOrganizationCodes || [];
    // 2列UIの選択キーを一覧検索In形式（組織コード_コード）へ変換
    return ObservableForkJoin([
      _isEmpty(lostReasonKeys)
        ? ObservableOf([] as string[])
        : this.resolveOrgMasterCodes(OrgMasterKind.LostReason, lostReasonKeys, orgCodes, true),
      _isEmpty(contactItemKeys)
        ? ObservableOf([] as string[])
        : this.resolveOrgMasterCodes(OrgMasterKind.ContactItem, contactItemKeys, orgCodes, true),
    ]).pipe(
      map(([lostIn, itemIn]) => {
        const next = _cloneDeep(dtl);
        next.contactExtractLostReasonCodeList = _isEmpty(lostReasonKeys)
          ? []
          : _uniq(_compact(_map(lostIn, value => _trim(_toString(value)))));
        next.contacExtractItemCodeList = _isEmpty(contactItemKeys)
          ? []
          : _uniq(_compact(_map(itemIn, value => _trim(_toString(value)))));
        // 一覧組織選択に合わせる（未選択＝全組織検索のとき組織コード配列は空のまま）
        next.organizationCodeList = selectedOrganizationCodes || [];
        if (!_isEmpty(selectedOrganizationCodes)) {
          next.organizationCode = selectedOrganizationCodes[0] || null;
        } else if (!_isEmpty(fallbackOrganizationCode)) {
          // データ定義で代表組織コードが必須のとき: 検索は全組織のまま、保存API用に代表組織のみ補填
          next.organizationCode = _trim(_toString(fallbackOrganizationCode));
        }
        return next;
      }),
    );
  }

  /**
   * 顧客に紐づく車両情報を取得します。
   * @param customerCode 顧客コード
   */
  getVehicleInfo(customerCode: string): Observable<BlListResponseBody<IVehicleInfo>> {
    const conditions: BlConditionInfo[] = [
      {
        key: 'customerCode',
        type: BlConditionType.Equal,
        value: customerCode
      },
      {
        key: 'logicalDeleteDiv',
        type: BlConditionType.Equal,
        value: LogicalDeleteDiv.Valid
      }
    ];
    const fields = ['vehicleMgtNumber'];
    return this.vehicleInfo.getAll<IVehicleInfo>({ conditions, fields }).pipe(
      map(response => {
        return response;
      })
    );
  }


  /**
   * コンタクト実施結果リストを検索して返します。
   * @param organizationCode 組織コード
   * @returns コンタクト実施結果リスト
   */
  searchContactExecuteResultList(organizationCode: string): Observable<IContactExecuteResult[]> {
    // キャッシュがあればそれを返却
    if (this.cacheContactExecuteResultListMap.has(organizationCode)) {
      return ObservableOf(this.cacheContactExecuteResultListMap.get(organizationCode));
    }

    // 検索条件
    const conditions = [
      { key: 'logicalDeleteDiv', value: LogicalDeleteDiv.Valid, type: BlConditionType.Equal },
      { key: 'organizationCode', value: organizationCode, type: BlConditionType.Equal },
      { key: 'contactCreateDiv', value: ContactCreateDiv.Manual, type: BlConditionType.Equal }
    ];

    // 取得フィールド
    const fields = [
      'logicalDeleteDiv',
      'organizationCode',
      'contactExecuteResultCode',
      'contactExecuteResult',
      'contactExecuteResultDiv'
    ];

    // ソート条件
    const sortFields = [
      { field: 'contactExecuteResultDispOrder', desc: false }
    ];

    return this.contactExecuteResultResource.getAll<IContactExecuteResult>({ conditions, fields, sortFields }).pipe(
      map(
        response => {
          this.cacheContactExecuteResultListMap.set(organizationCode, response.searchResultList);
          return response.searchResultList;
        }
      )
    );
  }

  /**
   * コンタクト失注理由リストを検索して返します。
   * @param organizationCode 組織コード
   * @returns コンタクト失注理由リスト
   */
  searchContactLostReasonList(organizationCode: string): Observable<IContactLostReason[]> {
    // キャッシュがあればそれを返却
    if (this.cacheContactLostReasonListMap.has(organizationCode)) {
      return ObservableOf(this.cacheContactLostReasonListMap.get(organizationCode));
    }

    const conditions: BlConditionInfo[] = [
      { key: 'organizationCode', value: organizationCode, type: BlConditionType.Equal }
    ];
    // 2 列 UI 用に必要属性のみ取得（件数上限は API デフォルト）
    const fields = ['contactLostReasonCode', 'contactLostReason'];
    // ソート条件
    const sortFields = [
      { field: 'contactLostReasonDispOrder', desc: false }
    ];
    return this.contactLostReasonResource.getAll<IContactLostReason>({
      conditions,
      sortFields,
      fields,
    }).pipe(
      map(
        response => {
          this.cacheContactLostReasonListMap.set(organizationCode, response.searchResultList);
          return response.searchResultList;
        }
      )
    );
  }

  /**
   * コンタクト内容リストを検索して返します。
   * @param organizationCode 組織コード
   * @returns コンタクト内容リスト
   */
  searchContactItemList(organizationCode: string): Observable<IContactItem[]> {
    // キャッシュがあればそれを返却
    if (this.cacheContactItemListMap.has(organizationCode)) {
      return ObservableOf(this.cacheContactItemListMap.get(organizationCode));
    }

    const conditions: BlConditionInfo[] = [
      { key: 'organizationCode', value: organizationCode, type: BlConditionType.Equal }
    ];
    // 2 列 UI 用に必要属性のみ取得（件数上限は API デフォルト）
    const fields = ['contactItemCode', 'contactItemName', 'contactCheckDiv'];
    // ソート条件
    const sortFields = [
      { field: 'contactItemDispOrder', desc: false },
      { field: 'contactItemCode', desc: false },
      { field: 'contactCheckDiv', desc: false },
    ];
    return this.contactItemResource.getAll<IContactItem>({
      conditions,
      sortFields,
      fields,
    }).pipe(
      map(
        response => {
          this.cacheContactItemListMap.set(organizationCode, response.searchResultList);
          return response.searchResultList;
        }
      )
    );
  }

  /**
   * 失注理由・コンタクト区分の2列UIでマスタを取得する組織コードを、組織コード昇順で返します（全組織共通の組織コードは除く）。
   * @param organizationCodes 検索条件の組織コード（空のときは組織情報の全組織を対象とする）
   * @returns 組織コードリスト
   */
  getOrgMasterTargetOrgCodes(organizationCodes: string[]): string[] {
    const requested = organizationCodes || [];
    let orgCodes: string[];
    if (_isEmpty(requested)) {
      if (!_isEmpty(this.selectableOrganizationCodes)) {
        orgCodes = this.selectableOrganizationCodes;
      } else {
        orgCodes = _map(this.organizationInfoList, info => info.organizationCode);
      }
    } else {
      orgCodes = _compact(_map(requested, code => _trim(_toString(code))));
    }
    const filtered = _sortBy(_reject(orgCodes, orgCode => orgCode === CompanyConst.ORGANIZATION_CODE_ALL_COMPANY));
    if (!_isEmpty(filtered)) {
      return filtered;
    }
    if (_isEmpty(requested)) {
      return filtered;
    }
    // 全組織コードのみ等でフィルタ後が空のときは、未選択時と同様に候補組織へフォールバック
    return this.getOrgMasterTargetOrgCodes([]);
  }

  /**
   * 組織マルチセレクトの候補を登録します（未選択＝全組織時に失注理由・コンタクト区分の2列UIと揃える）。
   * @param organizations 組織マルチセレクトの候補一覧
   */
  registerSelectableOrganizations(organizations: IOrganizationInformation[]): void {
    const list = organizations || [];
    const nextCodes = _compact(_map(list, org => org.organizationCode));
    const preserveOrgMasterCache = this.areSelectableOrganizationCodesEqual(this.selectableOrganizationCodes, nextCodes);
    this.selectableOrganizationCodes = nextCodes;
    // 候補組織が変わったときだけ2列UIキャッシュを捨てる（一覧→初期表示設定で同一候補のときは検索条件側の取得を流用）
    if (!preserveOrgMasterCache) {
      this.cacheOrgMasterItemsMap.clear();
      this.cacheContactLostReasonListMap.clear();
      this.cacheContactItemListMap.clear();
    }
    this.mergeOrganizationDisplayInfo(list);
  }

  /** 組織マルチセレクト候補コードが同一か（順序は無視） */
  private areSelectableOrganizationCodesEqual(previous: string[], next: string[]): boolean {
    const left = _sortBy(previous || []);
    const right = _sortBy(next || []);
    if (left.length !== right.length) {
      return false;
    }
    return _every(left, (code, index) => code === right[index]);
  }

  /**
   * 組織マルチセレクトの readyEvent 後、候補組織が登録済みかを返します。
   */
  hasRegisteredSelectableOrganizations(): boolean {
    return !_isEmpty(this.selectableOrganizationCodes);
  }

  /** 2列UI用の組織別マスタ候補キャッシュをクリアします（組織名称解決後の再取得用）。 */
  clearOrgMasterItemsCache(): void {
    this.cacheOrgMasterItemsMap.clear();
  }

  /** 組織名称の参照用リストに、マルチセレクト由来の組織情報をマージします。 */
  mergeOrganizationDisplayInfo(organizations: IOrganizationInformation[]): void {
    if (_isEmpty(organizations)) {
      return;
    }
    const byCode = new Map<string, IOrganizationInformation>();
    _forEach(this.organizationInfoList, info => byCode.set(info.organizationCode, info));
    _forEach(organizations, info => byCode.set(info.organizationCode, info));
    this.organizationInfoList = Array.from(byCode.values());
  }

  /**
   * 組織ごとの失注理由またはコンタクト内容マスタを取得し、2列UIの候補として返します。
   * 組織のマスタに無いコードは全組織共通マスタで補完します。
   * @param kind マスタの種類
   * @param searchConditionOrganizationCodes 検索条件の組織コード（空のときは候補組織へ展開）
   * @returns 候補リスト（組織コードの並び順）
   */
  searchOrgMasterItems(kind: OrgMasterKind, searchConditionOrganizationCodes: string[]): Observable<IOrgMasterItem[]> {
    const organizationCodes = this.getOrgMasterTargetOrgCodes(searchConditionOrganizationCodes || []);
    if (_isEmpty(organizationCodes)) {
      return ObservableOf([]);
    }
    const cacheKey = `${kind}:${organizationCodes.join(',')}`;
    const cached = this.cacheOrgMasterItemsMap.get(cacheKey);
    if (cached) {
      return ObservableOf(cached);
    }
    // 組織ごとの getAll。1組織失敗時は空配列にして他組織の候補は表示する（LostReason / ContactItem で型が異なるため分岐）
    const searchMasters = (orgCode: string): Observable<{ code: number, name: string }[]> => {
      if (kind === OrgMasterKind.LostReason) {
        return this.searchContactLostReasonList(orgCode).pipe(
          map(list => _map(list, m => ({
            code: m.contactLostReasonCode,
            name: m.contactLostReason,
          }))),
          catchError(() => ObservableOf([])),
        );
      }
      return this.searchContactItemList(orgCode).pipe(
        map(list => _map(list, m => ({
          code: m.contactItemCode,
          name: m.contactItemName,
        }))),
        catchError(() => ObservableOf([])),
      );
    };
    // 先頭は全組織共通マスタ。各組織マスタに無いコードを共通側から補完する
    const orgCodes = _concat([CompanyConst.ORGANIZATION_CODE_ALL_COMPANY], organizationCodes);
    return ObservableForkJoin(_map(orgCodes, searchMasters)).pipe(
      map(([commonMasters, ...orgMastersList]) => _flatMap(organizationCodes, (orgCode, index) => {
        const orgMasters = orgMastersList[index] || [];
        const commons = commonMasters || [];
        const orgName = this.getOrganizationName(orgCode, this.organizationInfoList);
        const masters = _concat(orgMasters, _reject(commons, common => _some(orgMasters, { code: common.code })));
        const nameCounts = _countBy(masters, 'name');
        return _map(masters, m => ({
          key: buildOrgMasterSelectionKey(orgCode, m.code),
          organizationCode: orgCode,
          code: m.code,
          name: m.name,
          label: nameCounts[m.name] > 1 ? `[${orgName}] ${m.name} (${m.code})` : `[${orgName}] ${m.name}`,
        }));
      })),
      catchError(() => ObservableOf([])),
      tap(items => {
        // 他テナント／組織名称未取得時に「[] 他社受注」が固定表示されないよう、組織名未解決（[] ラベル）と0件はキャッシュしない
        const hasEmptyOrgLabel = _some(items, item => _startsWith(item.label, '[]'));
        if (!_isEmpty(items) && !hasEmptyOrgLabel) {
          this.cacheOrgMasterItemsMap.set(cacheKey, items);
        }
      }),
    );
  }

  /**
   * 初期表示設定の一覧組織コード配列／代表組織コードを、一覧検索条件の組織コード配列へ正規化します。
   * 組織コード配列が設定済み（要素0含む）ならそのまま返す（0件＝全組織検索）。
   * 代表組織コードへのフォールバックは、組織コード配列が未設定の旧レコードのみ。
   */
  private normalizeInitDisplayOrganizationCodes(dtl: IContactInitDisplaySettingDtl): string[] {
    const orgList = dtl.organizationCodeList;
    // 組織コード配列が設定済みならそのまま返す
    if (Array.isArray(orgList)) {
      return orgList as string[];
    }
    // 代表組織コードが設定済みならそのまま返す
    if (!_isEmpty(dtl.organizationCode)) {
      return [String(dtl.organizationCode)];
    }
    // 組織コード配列が未設定の旧レコードのみ、代表組織コード1件から組織コード配列を復元
    return [];
  }

  /**
   * 旧形式の数値コード配列（API が配列以外を返した場合は空扱い）
   * @param value 保存済み検索条件の値
   * @returns 数値コード配列
   */
  private asInitDisplayNumericCodeArray(value: any): number[] {
    return Array.isArray(value) ? (value as number[]) : [];
  }

  /**
   * コンタクト抽出対象失注理由／コンタクト内容コード配列を検索In用の文字列配列へ正規化します。
   * @param value 保存済み検索条件の値
   * @returns 検索In用の文字列配列
   */
  normalizeContactExtractOrgMasterInCodes(
    value: Array<string | number> | string | number | null | undefined
  ): string[] {
    // 値が空のときは空配列を返却
    if (_isNil(value) || value === '') {
      return [];
    }
    // 値が配列でないときは単一値を正規化
    if (!Array.isArray(value)) {
      return this.normalizeOrgMasterInCodes(_trim(_toString(value)));
    }
    // 値が空のときは空配列を返却
    if (_isEmpty(value)) {
      return [];
    }
    // 値が配列のときは各要素を正規化
    return this.normalizeOrgMasterInCodes(_map(value, entry => _trim(_toString(entry))));
  }

  /**
   * 失注理由／コンタクト区分の検索In値を文字列配列に正規化します（旧保存形式の数値・単一値を吸収）。
   * @param value 保存済み検索条件の値
   */
  private normalizeOrgMasterInCodes(value: string[] | number | string | null | undefined): string[] {
    if (_isNil(value) || value === '' || value === 0) {
      return [];
    }
    if (Array.isArray(value)) {
      return _uniq(_compact(_map(value, v => _trim(_toString(v)))));
    }
    const single = _trim(_toString(value));
    return single === '' || single === '0' ? [] : [single];
  }

  /**
   * 一覧抽出APIのcパラ用に失注理由／コンタクト区分の検索In値へ変換します。
   * 検索組織が1件のときは組織コード_コードからコード部分のみにします（既存API仕様）。
   * @param inCodes 画面・初期表示設定上の検索In値
   * @param organizationCodes 検索条件の組織コード（未選択＝全組織のときは org 付きのまま）
   */
  private toContactListDispOrgMasterInValues(inCodes: string[] | undefined, organizationCodes: string[] | undefined): string[] {
    // 画面・初期表示設定上の検索In値を検索In用の文字列配列へ正規化
    const normalized = this.normalizeOrgMasterInCodes(inCodes);
    if (_isEmpty(normalized)) {
      return [];
    }
    // 検索条件の組織コードが複数のときは組織コード_コードのまま返却
    const selectedOrgCodes = _compact(_map(organizationCodes || [], code => _trim(_toString(code))));
    if (selectedOrgCodes.length !== 1) {
      return normalized;
    }
    // 検索条件の組織コードが1件のときは組織コード_コードからコード部分のみにして返却
    return _uniq(_compact(_map(normalized, value => {
      const sepIndex = value.lastIndexOf(ORG_CODE_PAIR_SEPARATOR);
      if (sepIndex > 0 && sepIndex < value.length - 1) {
        return value.substring(sepIndex + 1);
      }
      return value;
    })));
  }

  /**
   * 2列UIの選択キーを、マスタを再取得して検索Inに載せる値へ解決します。
   * 複数組織のときは「組織コード_コード」、単一組織のときはコードのみとします。
   * 選択があるのに該当するコードが無いとき、またはマスタ取得に失敗したときは該当なしの値のみを返します。
   * @param kind マスタの種類
   * @param keys 選択キー（組織コード_コード）
   * @param organizationCodes 検索条件の組織コード
   * @param forceOrgCodePairFormat true のとき常に「組織コード_コード」形式（初期表示設定のコンタクト抽出コード保存用）
   * @returns 検索Inに載せる値
   */
  resolveOrgMasterCodes(
    kind: OrgMasterKind,
    keys: string[],
    organizationCodes: string[],
    forceOrgCodePairFormat = false,
  ): Observable<string[]> {
    if (_isEmpty(keys)) {
      return ObservableOf([]);
    }
    // 選択キーを正規化
    const normalizedKeys = _uniq(_map(keys, key => normalizeOrgMasterSelectionKey(key)));
    // 検索条件の組織コードを取得
    const orgCodes = this.getOrgMasterTargetOrgCodes(organizationCodes);
    // マスタを検索
    return this.searchOrgMasterItems(kind, organizationCodes).pipe(
      // マスタをフィルタリング
      map(items => _uniq(_map(_filter(items, item => _includes(normalizedKeys, item.key)),
        // 組織コード_コードからコード部分のみにして返却
        item => (forceOrgCodePairFormat || orgCodes.length > 1)
          ? item.organizationCode + ORG_CODE_PAIR_SEPARATOR + item.code
          : String(item.code)))),
      // コードが空のときは該当なしコードのみにして0件化する
      map(codes => _isEmpty(codes) ? [ORG_MASTER_NOT_FOUND_CODE] : codes),
      // マスタ取得に失敗したときは該当なしコードのみにして0件化する
      catchError(() => ObservableOf([ORG_MASTER_NOT_FOUND_CODE])),
    );
  }

  /**
   * コンタクトサマリー情報を取得します。
   * @param condition　コンタクト一覧検索条件
   */
  getSummary(condition: ContactSearchCondition): Observable<IContactSummary> {
    const params = new BlApiQueryParams;
    params.setConditionInfo(this.setSearchCondition(condition));

    return this.contactListDispResource.get<IContactSummary>({ params: params, entityOptions: { exPath: 'summary' } }).pipe(
      map(res => res ? res : {})
    );
  }

  /**
   * 一覧抽出の現在ページを設定します。
   * @param pageNumber ページ番号（0始まり）
   * @param pageSize グリッドの1ページあたり件数
   */
  setExtractCurrentPage(pageNumber: number, pageSize: number): void {
    this.extractCurrentPageNumber = pageNumber;
    this.extractCurrentPageSize = pageSize > 0 ? pageSize : 50;
  }

  /**
   * 抽出開始（GET /contactlistdisp）を実行します。
   * @param condition コンタクト一覧検索条件
   */
  startExtract(condition: ContactSearchCondition): Observable<IContactListExtractProgress> {
    const apiParams = new BlApiQueryParams();
    const conditions: BlConditionInfo[] = this.setSearchCondition(condition);
    // DM案内可否はサマリAPIが未対応のため、一覧抽出のリクエストにのみ条件を付与する（架電一覧と同じ方式）
    if (condition.customerDmSendDiv !== '' && condition.customerDmSendDiv !== undefined) {
      conditions.push({ key: 'customerDmSendDiv', value: condition.customerDmSendDiv, type: BlConditionType.Equal });
    }
    if (condition.vehicleDmSendDiv !== '' && condition.vehicleDmSendDiv !== undefined) {
      conditions.push({ key: 'vehicleDmSendDiv', value: condition.vehicleDmSendDiv, type: BlConditionType.Equal });
    }
    apiParams.setConditionInfo(conditions);
    const div = _isNil(condition.sortOrderDiv) || condition.sortOrderDiv === ''
      ? SortOrderDiv.CustomerNameKana
      : condition.sortOrderDiv;
    if (div === SortOrderDiv.ContactScheduleDate) {
      apiParams.addSortField('contactScheduleDateTime', true);
    } else {
      apiParams.addSortField('customerNameKana1', false);
    }
    const params = this.buildContactListDispExtractQueryParams(apiParams);
    return this.contactListDispResource.get<IContactListExtractProgress>({
      params,
      entityOptions: { exPath: 'contactlistdisp' },
    }).pipe(
      catchError(e => {
        if (e.status === 504) {
          // タイムアウト時はダミーの進捗を返してポーリングに移行させる
          return ObservableOf({} as IContactListExtractProgress);
        }
        // 抽出依頼受付・抽出中の再実行など TooManyRequests のとき、HTTP エラー本文は進捗エンティティ（errorContent を含む）
        if (e.status === HttpStatus.TooManyRequests) {
          const progress = !_isNil(e.response) ? e.response.error as IContactListExtractProgress : null;
          if (!_isNil(progress) && this.isNonEmptyErrorContent(progress)) {
            return ObservableThrow(this.throwExtractFailureFromProgress(progress, e));
          }
          return ObservableThrow(e);
        }
        // 件数上限超過など 400 のとき、HTTP エラー本文に contactListExtractProgressDiv はないが
        // サーバは抽出進捗に CountOverError を保存するため、進捗 GET で失敗ストリーム用オブジェクトに載せ替える。
        if (e.status === HttpStatus.BadRequest) {
          // catchError は進捗 GET 失敗時のみ適用する（mergeMap より後ろに置くと、付け替え済みエラーも元の e に戻してしまう）
          return this.getExtractProgress().pipe(
            catchError(() => ObservableThrow(e)),
            mergeMap(progress => {
              if (!_isNil(progress) && this.isExtractProgressDivCountOver(progress.contactListExtractProgressDiv)) {
                return ObservableThrow(this.throwExtractFailureFromProgress(progress, e));
              }
              return ObservableThrow(e);
            })
          );
        }
        return ObservableThrow(e);
      })
    );
  }

  /**
   * GET /contactlistdisp 用クエリ（clientSessionId + BlApiQueryParams をフラットな文字列マップにしたもの）
   */
  private buildContactListDispExtractQueryParams(params: BlApiQueryParams): Record<string, string> {
    const query: Record<string, string> = {
      clientSessionId: this.clientSessionId,
    };
    const qp = params.getURLSearchParams();
    qp.keys().forEach(key => {
      const v = qp.get(key);
      if (v != null) {
        query[key] = v;
      }
    });
    return query;
  }

  /**
   * 進捗 getById 用のパス ID。DM抽出進捗と同様にテナントは含めず {@code blUserId_clientSessionId} とする。
   */
  private buildContactListExtractProgressPathId(blUserId: string): string {
    return StringUtils.concatWith('_', blUserId, this.clientSessionId);
  }

  /**
   * 抽出進捗のチェックボックス選択件数を数値として返します（未設定時は 0）。
   * @param progress 抽出進捗
   */
  private resolveCheckboxSelectCount(progress: IContactListExtractProgress): number {
    const n = progress.selectedCheckboxCount;
    if (_isNil(n) || n < 0) {
      return 0;
    }
    return n;
  }

  /**
   * 抽出進捗を取得します（{@link ContactListExtractProgressResource#getById}）。
   * @returns 抽出進捗
   */
  getExtractProgress(): Observable<IContactListExtractProgress> {
    if (_isNil(this.clientSessionId)) {
      return ObservableThrow(this.toExtractBlApiError(''));
    }
    return this.loginResourceService.getLoginUserEmployeeBindModel().pipe(
      mergeMap(user => {
        if (_isEmpty(user.blUserId) || _isEmpty(user.blTenantId)) {
          return ObservableThrow(this.toExtractBlApiError(''));
        }
        const progressId = this.buildContactListExtractProgressPathId(user.blUserId);
        return this.contactListExtractProgressResource.getById<IContactListExtractProgress>(progressId);
      })
    );
  }

  /**
   * 抽出進捗を一定間隔で取得し続けます（ポーリング用）。
   */
  startCheckProgress(): Observable<IContactListExtractProgress> {
    return ObservableTimer(0, 3000).pipe(
      mergeMap(() => this.getExtractProgress())
    );
  }

  /**
   * 下書き1バッチ分の一覧を取得します（GET /draft/{businessSessionId}）。
   * @param businessSessionId 業務セッションID
   * @param globalFirstIndexZeroBased 検索結果全体の先頭からの0始まりインデックス（行番号は +1 して付与）
   */
  getDraftContactListBySessionId(businessSessionId: string, globalFirstIndexZeroBased = 0): Observable<IContactEx[]> {
    return this.contactListDispResource.get<IDraftContactListDisp>({
      entityOptions: { operation: 'draft', id: businessSessionId },
    }).pipe(
      map(draft => {
        const contactList = draft && !_isNil(draft.contactList) ? draft.contactList : [];
        return _map<IContact, IContactEx>(contactList, (contact, idx) => {
          return _assign(contact, {
            gridData: this.convertData(contact, globalFirstIndexZeroBased + idx + 1)
          });
        });
      })
    );
  }

  /**
   * 一覧抽出を中断します（PUT …/cancelextract。サーバ内で進捗取得→Cancel 更新の 1 往復）。
   */
  cancelExtract(): Observable<void> {
    if (_isNil(this.clientSessionId)) {
      return ObservableOf(undefined);
    }
    return this.contactListDispResource.put<{}>(
      { clientSessionId: this.clientSessionId },
      { entityOptions: { operation: 'cancelextract' } },
    ).pipe(
      map(() => undefined),
      catchError(() => ObservableOf(undefined)),
    );
  }

  /**
   * 一覧抽出進捗が終端（完了／エラー／件数上限超過エラー／キャンセル）でない場合のみ、抽出を中断（Cancel）します。
   * 完了済みの進捗を無条件に Cancel に上書きしないよう {@link cancelExtract} とは異なります。
   * 一括処理完了後の再抽出の直前に、clientSessionIdに紐づく進行中抽出を片付けるために使用します。
   */
  cancelExtractIfInFlight(): Observable<void> {
    if (_isNil(this.clientSessionId)) {
      return ObservableOf(undefined);
    }
    return this.contactListDispResource.put<{}>(
      { clientSessionId: this.clientSessionId, onlyIfInFlight: true },
      { entityOptions: { operation: 'cancelextract' } },
    ).pipe(
      map(() => undefined),
      catchError(() => ObservableOf(undefined)),
    );
  }

  /**
   * 現在ページの下書きのみ再取得します（下書きバッチ件数に応じた単一／複数取得）。
   */
  loadCurrentPageDraft$(): Observable<IContactEx[]> {
    return this.loadDraftsForCurrentExtractPage();
  }

  /**
   * コンタクト一覧表示情報を取得します。
   * @param condition　コンタクト一覧検索条件
   */
  getContactListDisp(condition: ContactSearchCondition): Observable<IContactEx[]> {
    this.prepareNewListExtractSession();
    return this.startExtract(condition).pipe(
      // 204 No Content 等でボディが null になる場合があるため、null を 0 件完了として扱う
      map(extractRes =>
        _isNil(extractRes) ? this.createZeroResultTerminalProgress() : extractRes
      ),
      mergeMap(extractRes => {
        if (this.isNonEmptyErrorContent(extractRes)) {
          return ObservableThrow(this.throwExtractFailureFromProgress(extractRes));
        }
        if (this.isTerminalExtractProgress(extractRes)) {
          return ObservableOf(extractRes);
        }
        return this.waitExtractUntilTerminal$();
      }),
      mergeMap(progress => {
        if (this.isNonEmptyErrorContent(progress)) {
          return ObservableThrow(this.throwExtractFailureFromProgress(progress));
        }
        if (progress.contactListExtractProgressDiv === ContactListExtractProgressDiv.Cancel) {
          return ObservableOf([]);
        }
        if (progress.contactListExtractProgressDiv === ContactListExtractProgressDiv.Error
          || progress.contactListExtractProgressDiv === ContactListExtractProgressDiv.CountOverError) {
          return ObservableThrow(this.throwExtractFailureFromProgress(progress));
        }
        this.lastExtractTotalCount = !_isNil(progress.totalCount) ? progress.totalCount : 0;
        this.lastBusinessSessionIdList = !_isNil(progress.businessSessionIdList)
          ? progress.businessSessionIdList
          : [];
        // 総件数が0の場合は空配列を返す
        if (this.lastExtractTotalCount === 0) {
          return ObservableOf([]);
        }
        // 現在のグリッドページに必要な下書きバッチを取得
        return this.loadDraftsForCurrentExtractPage();
      })
    );
  }

  /**
   * clientSessionId を再採番し、現在ページを0に初期化します。
   */
  private prepareNewListExtractSession(): void {
    this.clientSessionId = UUID.UUID();
    this.extractCurrentPageNumber = 0;
    this.bulkUpdateResultStatusByTargetKey.clear();
  }

  /**
   * 一覧抽出セッションのクライアントセッション ID を返します。
   *
   * 抽出のたびに prepareNewListExtractSession() で 1 回だけ採番し、
   * 同一セッション中の一括更新・削除API、チェックボックスの選択状態変更APIでは常にこの値を使います。
   * （上記APIの呼び出しごとに再採番はしない）
   */
  getClientSessionId(): string | undefined {
    return this.clientSessionId;
  }

  /**
   * コンタクト一覧の選択状態変更 API（POST …/changeselection）を 1 回呼び出します。
   *
   * @param request クライアントセッション ID・区分・（区分 2/3/4 時は）業務セッション ID とコンタクト情報番号配列（IContactUpdDelTargetChangeInfo）
   */
  changeContactListSelection(request: IContactUpdDelTargetChangeInfo): Observable<{}> {
    return this.contactListDispResource.post<{}>(
      request,
      { entityOptions: { operation: 'changeselection' } }
    );
  }

  /**
   * 数値または数値として解釈できる文字列を number に変換します。解釈できない場合は NaN です。
   * （例: チェック列の value＝グローバル行インデックス、下書き行の contactInfoNo）
   */
  private parseToNumber(raw: any): number {
    if (typeof raw === 'number') {
      return raw;
    }
    return parseInt(String(raw), 10);
  }

  /**
   * 区分 2（ページ変更）／3（削除前同期）／4（編集前同期）について、画面のチェック内容をサーバ側下書きへ保存します。
   *
   * 当メソッドは「1 業務セッション＝下書き1ブロックあたり最大 CONTACT_LIST_EXTRACT_BATCH_SIZE 件」単位で、
   * {@code contactInfoNoList} に**含まれたコンタクト情報番号だけ true、リストに無い番号はすべて false** とします。
   * そのため、現在グリッドに載っている数十件だけを送ると、同じブロック内の非表示行の選択が誤って外れます。
   * 対策として、(1) 当該ブロックの下書きを全件 GET し、(2) 画面上の行はチェックボックス、(3) 画面外の行はサーバの
   * {@code selectStatus} を使って「送るべき番号一覧」を組み立ててから POST します。
   *
   * グリッドが下書きバッチ境界をまたぐ場合はブロックが複数になるため、{@code concatMap} でブロック順に API を直列実行します
   * （進捗のチェックボックス件数がブロックごとに差分更新されるため）。
   *
   * @param updDelTargetOperationDiv UpdDelTargetOperationDiv の Paging / Delete / Edit（文字列 "2" "3" "4"）
   * @param visibleGridRows 現在ページに表示している行（改ページ直前・削除／編集直前のスナップショット）
   */
  syncPagedSelectionToServer(updDelTargetOperationDiv: string, visibleGridRows: IContactEx[]): Observable<void> {
    const clientSessionId = this.getClientSessionId();
    const businessSessionIdList = this.lastBusinessSessionIdList;
    // 抽出完了前やセッション欠落時は API を呼べない
    if (_isEmpty(clientSessionId) || _isNil(businessSessionIdList) || businessSessionIdList.length === 0) {
      return ObservableThrow(this.toExtractBlApiError('一覧の抽出セッションが無効です。'));
    }
    // 本メソッドの対象は区分 2/3/4 のみ（0/1 は changeContactListSelection を単体で呼ぶ）
    if (updDelTargetOperationDiv !== UpdDelTargetOperationDiv.Paging
      && updDelTargetOperationDiv !== UpdDelTargetOperationDiv.Delete
      && updDelTargetOperationDiv !== UpdDelTargetOperationDiv.Edit) {
      return ObservableThrow(this.toExtractBlApiError(''));
    }
    const batchSize = this.getEffectiveDraftBlockSpanForPaging(
      this.lastExtractTotalCount,
      businessSessionIdList.length
    );
    // 表示行のグローバル行番号（checkParam.value）から、触る必要のある下書きブロック番号（0 始まり）を集める
    const batchIndexSet = new Set<number>();
    _forEach(visibleGridRows, row => {
      // グローバル行番号
      const globalRowIndex = this.parseToNumber(row.gridData.checkParam.value);
      if (isNaN(globalRowIndex) || globalRowIndex < 0) {
        return;
      }
      batchIndexSet.add(Math.floor(globalRowIndex / batchSize));
    });
    // ブロック番号リストをソート
    const batches = Array.from(batchIndexSet).sort((a, b) => a - b);
    // 表示行が無い／境界が取れない場合はサーバへ送る変更が無いので何もしない
    if (batches.length === 0) {
      return ObservableOf(undefined);
    }
    if (_some(batches, b => b >= businessSessionIdList.length)) {
      return ObservableThrow(this.toExtractBlApiError(''));
    }
    // ブロックごとに：下書き全件取得 → マージ済み contactInfoNoList で changeselection
    return observableFrom(batches).pipe(
      concatMap(batchIndex => {
        // 業務セッションID
        const businessSessionId = businessSessionIdList[batchIndex];
        // グローバル開始インデックス
        const globalStart = batchIndex * batchSize;
        // 下書き全件取得
        return this.getDraftContactListBySessionId(businessSessionId, globalStart).pipe(
          mergeMap(fullBatch => {
            // コンタクト情報番号配列の組み立て
            const contactInfoNoList = this.buildContactInfoNoListForBatchMerge(fullBatch, visibleGridRows);
            // リクエストボディの組み立て
            const body: IContactUpdDelTargetChangeInfo = {
              clientSessionId,
              businessSessionId,
              updDelTargetOperationDiv,
              contactInfoNoList,
            };
            // コンタクト一覧の選択状態変更APIを呼び出し
            return this.changeContactListSelection(body);
          }),
          map(() => undefined)
        );
      }),
      map(() => undefined)
    );
  }

  /**
   * 1 つの下書きブロック（最大 CONTACT_LIST_EXTRACT_BATCH_SIZE 件）について、changeselection に渡す「選択済みコンタクト情報番号」の配列を組み立てます。
   *
   * 【処理の流れ】
   * 1. 現在グリッドに載っている行について、グローバル行インデックス → 画面上のチェック ON/OFF の対応表を作る。
   * 2. 下書きブロック内の各行について、
   *    - 手順 1 の表に行があれば → 画面上のチェック状態を採用。
   *    - 無ければ（このページでは表示していない行）→ サーバが保持している選択状態を採用。
   * 3. 選択状態とみなした行の コンタクト情報番号 だけを配列に詰めて返す（未選択は配列に含めない）。
   *
   * @param fullBatch 当該業務セッションの下書き 1 ブロック分（GET draft の全行）
   * @param visibleGridRows 現在グリッドに表示している行の一覧（うち当該ブロックに属する行だけがマージに使われる）
   */
  private buildContactInfoNoListForBatchMerge(
    fullBatch: IContactEx[],
    visibleGridRows: IContactEx[]
  ): number[] {
    // 手順 1: グローバル行番号（convertData で value に入れている値）→ 画面上のチェック状態
    const screenCheckedByGlobalRowIndex = new Map<number, boolean>();
    _forEach(visibleGridRows, row => {
      // グローバル行番号
      const globalRowIndex = this.parseToNumber(row.gridData.checkParam.value);
      if (isNaN(globalRowIndex) || globalRowIndex < 0) {
        return;
      }
      screenCheckedByGlobalRowIndex.set(globalRowIndex, row.gridData.checkParam.checked === true);
    });
    const selectedContactInfoNos: number[] = [];
    // 手順 2〜3: ブロック全行を走査し、選択とみなすコンタクト情報番号だけを列挙
    _forEach(fullBatch, row => {
      // グローバル行番号（後続の選択状態で使うため、数値に変換後のチェックは行わない）
      const globalRowIndex = this.parseToNumber(row.gridData.checkParam.value);
      // コンタクト情報
      const draftContact = row.gridData.contact as IContact & {
        contactInfoNo?: number | null;
        selectStatus?: boolean | null;
      };
      // コンタクト情報番号
      const contactInfoNoRaw = draftContact.contactInfoNo;
      if (_isNil(contactInfoNoRaw)) {
        return;
      }
      // コンタクト情報番号を数値に変換
      const contactInfoNo = this.parseToNumber(contactInfoNoRaw);
      if (isNaN(contactInfoNo)) {
        return;
      }
      // 画面上に行があるかどうか
      const isRowVisibleOnScreen = !isNaN(globalRowIndex) && screenCheckedByGlobalRowIndex.has(globalRowIndex);
      // 選択状態かどうか。画面上に行がある場合は画面上のチェック状態を採用、無ければサーバが保持している選択状態を採用
      const isSelected = isRowVisibleOnScreen
        ? screenCheckedByGlobalRowIndex.get(globalRowIndex)
        : draftContact.selectStatus === true;
      // 選択状態の場合はコンタクト情報番号を配列に追加
      if (isSelected) {
        selectedContactInfoNos.push(contactInfoNo);
      }
    });
    return selectedContactInfoNos;
  }

  /**
   * 進捗の {@code totalCount} と {@code businessSessionIdList} から、グローバル行 → 下書きインデックス写像用の幅を求めます。
   * バックエンドは抽出スライスごとに下書きを積み上げるため、業務セッション本数が
   * 「total を最大下書き件数で割った最小本数」より大きい場合、
   * CONTACT_LIST_EXTRACT_BATCH_SIZE 件固定前提ではページングが破綻します。
   */
  private getEffectiveDraftBlockSpanForPaging(total: number, businessSessionCount: number): number {
    if (businessSessionCount <= 0 || total <= 0) {
      return ContactListPageService.CONTACT_LIST_EXTRACT_BATCH_SIZE;
    }
    const minSessionsIfEachDraftMax = Math.ceil(total / ContactListPageService.CONTACT_LIST_EXTRACT_BATCH_SIZE);
    if (businessSessionCount > minSessionsIfEachDraftMax) {
      return Math.max(1, Math.ceil(total / businessSessionCount));
    }
    return ContactListPageService.CONTACT_LIST_EXTRACT_BATCH_SIZE;
  }

  /**
   * 複数下書きを結合してスライスしたページ行に、検索結果全体での 0 始まりグローバル行番号を付け直します。
   * （近似ブロック幅で取得した行の tempId / checkParam がずれるため）
   */
  private applyGlobalIndicesToSlicedPageRows(rows: IContactEx[], firstIndexZeroBased: number): IContactEx[] {
    _forEach(rows, (row, i) => {
      const gid = firstIndexZeroBased + i;
      const oneBased = gid + 1;
      if (!_isNil(row.gridData)) {
        row.gridData.tempId = oneBased;
        const cp = row.gridData.checkParam;
        if (!_isNil(cp)) {
          cp.value = gid;
        }
      }
    });
    return rows;
  }

  /**
   * 現在のグリッドページに必要な下書きバッチを取得します（単一または複数バッチを結合）。
   */
  private loadDraftsForCurrentExtractPage(): Observable<IContactEx[]> {
    // 業務セッションIDリスト
    const list = this.lastBusinessSessionIdList;
    // 総件数
    const total = this.lastExtractTotalCount;
    // グリッドページあたり件数
    const pageSize = this.extractCurrentPageSize;
    // グリッドページ番号
    const pageIndex = this.extractCurrentPageNumber;

    // 業務セッションIDリストがnullまたは空、総件数が0以下、グリッドページあたり件数が0以下の場合は空配列を返す
    if (_isNil(list) || list.length === 0 || total <= 0 || pageSize <= 0) {
      return ObservableOf([]);
    }

    // グリッドページの先頭インデックス = グリッドページ番号 * グリッドページあたり件数
    const firstIndex = pageIndex * pageSize;
    // グリッドページの先頭インデックスが総件数以上の場合は空配列を返す
    if (firstIndex >= total) {
      return ObservableOf([]);
    }

    // グリッドページの最後インデックス = Math.min(グリッドページの先頭インデックス + グリッドページあたり件数 - 1, 総件数 - 1)
    const lastIndex = Math.min(firstIndex + pageSize - 1, total - 1);
    // バッチサイズ（業務セッション本数が多い抽出ではスライス単位で 1 本ずつ増えるため幅を近似）
    const batchSize = this.getEffectiveDraftBlockSpanForPaging(total, list.length);
    // バッチの開始インデックス = Math.floor(グリッドページの先頭インデックス / バッチサイズ)
    const startBatch = Math.floor(firstIndex / batchSize);
    // バッチの最後インデックス = Math.floor(グリッドページの最後インデックス / バッチサイズ)
    const endBatch = Math.floor(lastIndex / batchSize);

    // バッチのインデックスリストを作成
    const batchIndices: number[] = [];
    for (let b = startBatch; b <= endBatch; b++) {
      batchIndices.push(b);
    }

    // バッチのインデックスリストの要素が業務セッションIDリストの要素数以上の場合は空配列を返す
    if (_some(batchIndices, b => b >= list.length)) {
      return ObservableOf([]);
    }

    // 1回の下書き取得で返ってくる件数は最大 batchSize 件分。総件数がそれ以下なら総件数分取得出来るが、
    // 画面表示件数分のみを表示するため、検索結果全体の [firstIndex, lastIndex] に対応する行だけを切り出す。
    const sliceToGridPage = (combined: IContactEx[]): IContactEx[] => {
      // チャンクのグローバル開始インデックス
      const chunkGlobalStart = startBatch * batchSize;
      // チャンクのグローバル開始インデックスからグリッドページの先頭インデックスを引いた値 = チャンクのグローバル開始インデックスからグリッドページの先頭インデックスまでのインデックス
      const from = firstIndex - chunkGlobalStart;
      // チャンクのグローバル開始インデックスからグリッドページの最後インデックスを引いた値 + 1 = チャンクのグローバル開始インデックスからグリッドページの最後インデックスまでのインデックス
      const to = lastIndex - chunkGlobalStart + 1;
      // チャンクのグローバル開始からグリッドページの先頭までのインデックスが0未満、
      // またはチャンクのグローバル開始からグリッドページの最後までのインデックスがcombinedの要素数以上の場合は空配列を返す
      if (from < 0 || from >= combined.length) {
        return [];
      }
      return combined.slice(from, Math.min(to, combined.length));
    };

    // バッチのインデックスリストの要素が1件の場合
    if (batchIndices.length === 1) {
      const b = batchIndices[0];
      // 下書き1バッチ分の一覧を取得して返す
      return this.getDraftContactListBySessionId(list[b], b * batchSize).pipe(
        map(contacts => sliceToGridPage(contacts)),
        map(sliced => this.applyCachedBulkResultStatusToRows(sliced)),
        map(sliced => this.applyGlobalIndicesToSlicedPageRows(sliced, firstIndex))
      );
    }

    // バッチが複数件の場合
    // 下書きを該当バッチ分だけ取得する
    const requests = _map(batchIndices, b =>
      this.getDraftContactListBySessionId(list[b], b * batchSize)
    );

    // 下書きを該当バッチ分だけ取得した結果を結合して返す
    return ObservableForkJoin(requests).pipe(
      map((parts: IContactEx[][]) => {
        const combined = parts.reduce((a, b) => a.concat(b), [] as IContactEx[]);
        const sliced = this.applyCachedBulkResultStatusToRows(sliceToGridPage(combined));
        return this.applyGlobalIndicesToSlicedPageRows(sliced, firstIndex);
      })
    );
  }


  /**
   * 下書き取得行に、メモリ保持中の一括結果を反映する（ページ遷移後も処理状況列を保つため）。
   */
  private applyCachedBulkResultStatusToRows(rows: IContactEx[]): IContactEx[] {
    _forEach(rows, (row) => {
      const pk = this.buildRowPkForBulkResult(row);
      row.gridData.updateStatus = _defaultTo(this.bulkUpdateResultStatusByTargetKey.get(pk), '');
    });
    return rows;
  }

  /**
   * 進捗情報にエラー内容があるかどうかを判定します。
   * @param p 進捗情報
   */
  private isNonEmptyErrorContent(p: IContactListExtractProgress): boolean {
    return !_isNil(p) && !_isEmpty((_trim(p.errorContent || '')));
  }

  /**
   * 進捗情報が終端状態かどうかを判定します。
   * @param p 進捗情報
   */
  private isTerminalExtractProgress(p: IContactListExtractProgress): boolean {
    if (_isNil(p)) {
      return false;
    }
    const div = p.contactListExtractProgressDiv;
    return div === ContactListExtractProgressDiv.Complete
      || div === ContactListExtractProgressDiv.Error
      || div === ContactListExtractProgressDiv.CountOverError
      || div === ContactListExtractProgressDiv.Cancel;
  }

  /**
   * 抽出開始 API の応答が null（204 等）のとき後段で 0 件完了として扱うための進捗
   */
  private createZeroResultTerminalProgress(): IContactListExtractProgress {
    return {
      contactListExtractProgressDiv: ContactListExtractProgressDiv.Complete,
      totalCount: 0,
      businessSessionIdList: [],
    };
  }

  /**
   * 進捗情報が終端状態になるまで待ちます。
   */
  private waitExtractUntilTerminal$(): Observable<IContactListExtractProgress> {
    return this.startCheckProgress().pipe(
      filter(p => this.isTerminalExtractProgress(p)),
      take(1)
    );
  }

  /**
   * 一覧抽出進捗を失敗ストリーム用の {@link BlApiErrorInfo} に変換し、画面上で進捗区分を識別できるよう
   * {@code contactListExtractProgressDiv} を付与します。
   * {@link isContactListExtractDraftLimitExceededError} はこの付与済みオブジェクトのみを見てワーニング可否を判定します。
   *
   * @param progress GET 進捗／ポーリングで得たコンタクト一覧抽出進捗（区分・{@code errorContent} を参照）
   * @param preserveOriginalBlApiError 互換用。指定時は {@code isApi}／{@code response} 等を引き継ぎつつメッセージと区分を上書きする
   * @returns メッセージ・HTTP ステータス・{@code contactListExtractProgressDiv} を保持したオブジェクト
   */
  private throwExtractFailureFromProgress(
    progress: IContactListExtractProgress,
    preserveOriginalBlApiError?: BlApiErrorInfo
  ): BlApiErrorInfoWithContactListExtractDiv {
    // サーバが errorContent に解決済み文言を載せる。空のときは汎用メッセージにフォールバックする。
    const trimmedErrorContent = _trim(progress.errorContent || '');
    const text = !_isEmpty(trimmedErrorContent)
      ? trimmedErrorContent
      : '一覧の抽出に失敗しました。';
    const payload = {
      messages: [text],
      status: HttpStatus.BadRequest,
      contactListExtractProgressDiv: progress.contactListExtractProgressDiv,
    } as BlApiErrorInfoWithContactListExtractDiv;
    if (!_isNil(preserveOriginalBlApiError)) {
      return _assign({}, preserveOriginalBlApiError, payload) as BlApiErrorInfoWithContactListExtractDiv;
    }
    return payload;
  }

  /**
   * BL API のエラーが「抽出上限超過」（下書き登録件数上限）に起因する一覧抽出失敗かどうかを返します。
   * メッセージ本文ではなく {@link ContactListExtractProgressDiv#CountOverError} の区分値のみで判定します。
   *
   * @param err 画面上で表示するためのエラー情報（進捗由来、または 400 時の {@link startExtract} 内の進捗 GET 付け替え後に区分が付く）
   * @returns 上限超過区分なら {@code true}、それ以外・{@code undefined}・区分未設定は {@code false}
   */
  isContactListExtractDraftLimitExceededError(err: BlApiErrorInfo | null | undefined): boolean {
    // 区分が未付与のエラー（進捗に CountOverError がない経路）はワーニング対象外
    if (_isNil(err)) {
      return false;
    }
    return this.isExtractProgressDivCountOver(
      (err as BlApiErrorInfoWithContactListExtractDiv).contactListExtractProgressDiv
    );
  }

  /**
   * 進捗区分が「抽出上限超過」（{@link ContactListExtractProgressDiv#CountOverError}）か。
   * JSON で数値化された区分値とも突き合わせられるよう文字列化して比較する。
   */
  private isExtractProgressDivCountOver(div: string | number | null | undefined): boolean {
    return String(_isNil(div) ? '' : div) === String(ContactListExtractProgressDiv.CountOverError);
  }

  /**
   * 抽出APIのエラー内容を変換します。
   * @param message エラーメッセージ
   */
  private toExtractBlApiError(message: string): BlApiErrorInfo {
    const text = !_isEmpty(_trim(message || '')) ? _trim(message) : '一覧の抽出に失敗しました。';
    return {
      messages: [text],
      status: HttpStatus.BadRequest,
    } as BlApiErrorInfo;
  }

  /**
   * コンタクト一覧を一括更新します。
   * コンタクト一覧からの一括では {@code contactList} は送らず、{@code clientSessionId} と {@code itemValuePairList} のみで呼び出す。
   * 処理開始時に進捗（処理中・件数0／合計＝抽出進捗のチェックボックス選択件数）を {@link bulkUpdateProgressSubject} で通知する。
   * @param contactMultiplePartialUpdate　コンタクト一覧更新内容
   */
  updateContact(contactMultiplePartialUpdate: IContactMultiplePartialUpdate): Observable<IContactMultiplePartialUpdate> {

    return this.getExtractProgress().pipe(
      mergeMap(extractProgress => {
        // 初期化した進捗状況を最初に送信（合計件数はコンタクト一覧抽出進捗状況のチェックボックス選択件数。
        const progress: IBulkUpdateProgress = {
          bulkUpdateManageId: this.bulkUpdateManageId,
          processCount: 0,
          totalCount: this.resolveCheckboxSelectCount(extractProgress),
          bulkUpdateStatus: BulkUpdateStatus.Processing
        };
        this.bulkUpdateProgressSubject.next(progress);

        // 更新APIコール
        this.bulkUpdateManageId = UUID.UUID();
        contactMultiplePartialUpdate.bulkUpdateWebSocketId = this.bulkUpdateWebSocketId;
        contactMultiplePartialUpdate.bulkUpdateManageId = this.bulkUpdateManageId;
        contactMultiplePartialUpdate.clientSessionId = this.clientSessionId;

        return this.contactResource.patch<IContactMultiplePartialUpdate>(
          contactMultiplePartialUpdate,
          { entityOptions: { operation: 'multiplepartupdate' } }
        );
      })
    );
  }

  /**
   * コンタクト一覧を一括削除します。
   * コンタクト一覧からの一括では {@code contactList} は送らず、{@code clientSessionId} のみで呼び出す。
   * 処理開始時に進捗（処理中・件数0／合計＝抽出進捗のチェックボックス選択件数）を {@link bulkUpdateProgressSubject} で通知する。
   * @param contactMultipleDelete コンタクト情報一括削除
   */
  deleteContact(contactMultipleDelete: IContactMultipleDelete): Observable<IContactMultipleDelete> {

    return this.getExtractProgress().pipe(
      mergeMap(extractProgress => {
        // 初期化した進捗状況を最初に送信（合計件数はコンタクト一覧抽出進捗状況のチェックボックス選択件数。
        const progress: IBulkUpdateProgress = {
          bulkUpdateManageId: this.bulkUpdateManageId,
          processCount: 0,
          totalCount: this.resolveCheckboxSelectCount(extractProgress),
          bulkUpdateStatus: BulkUpdateStatus.Processing
        };
        this.bulkUpdateProgressSubject.next(progress);

        // 削除APIコール
        this.bulkUpdateManageId = UUID.UUID();
        contactMultipleDelete.bulkUpdateWebSocketId = this.bulkUpdateWebSocketId;
        contactMultipleDelete.bulkUpdateManageId = this.bulkUpdateManageId;
        contactMultipleDelete.clientSessionId = this.clientSessionId;

        return this.contactResource.deleteWithBody<IContactMultipleDelete>(
          contactMultipleDelete,
          { entityOptions: { operation: 'multipledelete' } }
        );
      })
    );
  }

  /**
   * csvファイルをダウンロードします。
   * @param condition　検索条件
   */
  export(condition: ContactSearchCondition): any {
    const params = new BlApiQueryParams;
    params.setConditionInfo(this.setSearchCondition(condition));
    return this.exportConditionInfoResource.get<{ notificationPushService: NotificationPushService }>(
      { params: params, entityOptions: { exPath: 'contactcsv' } }
    );
  }

  /**
   * コンタクト一覧印刷APIを呼び出します。
   */
  printContactList(genericPrintInstruction: IGenericPrintInstruction, condition: ContactSearchCondition): Observable<IReportOutputResult> {
    const printInstruction: IContactListPrintInstruction = this.createPrintInstruction(genericPrintInstruction, condition);
    return this.contactListDispResource.put<IReportOutputResult>(
      printInstruction,
      { entityOptions: { operation: 'print' } }
    ).pipe(
      map(
        contactListPrintResult => {
          return !_isNil(contactListPrintResult) ? contactListPrintResult : null;
        }
      ),
      catchError((error: BlApiErrorInfo) => {
        // 上限超過エラーの判定
        if (error.status === HttpStatus.BadRequest && error.messages && error.messages.length > 0) {
          return this.dialogService.warn(error.messages[0]).pipe(
            mergeMap(ref => {
              if (ref.reason === ModalReason.Done) {
                ref.hide();
              }
              return ObservableThrow(error);
            })
          );
        }
        return ObservableThrow(error);
      })
    );
  }

  /**
   * コンタクト初期表示設定登録・更新します。
   * isRegistがtrueの場合、登録
   * @param contactInitDisplaySettingEx　コンタクト初期表情報
   */
  updateContactInitDisplaySetting(contactInitDisplaySettingEx: IContactInitDisplaySettingEx): Observable<IContactInitDisplaySetting> {
    if (contactInitDisplaySettingEx.isClear) {
      return ObservableOf(null);
    }
    const contactInitDisplaySetting: IContactInitDisplaySetting = _cloneDeep(contactInitDisplaySettingEx);
    if (contactInitDisplaySettingEx.isRegist) {
      return this.contactInitDisplaySettingResource.post<IContactInitDisplaySetting>(contactInitDisplaySetting);
    } else {
      return this.contactInitDisplaySettingResource.putById<IContactInitDisplaySetting>(
        contactInitDisplaySetting.employeeCode,
        contactInitDisplaySetting
      );
    }
  }

  /**
   * コンタクト背景色設定を登録・更新します。
   * @param contactStatusBgColorSetting　コンタクト状況背景色設定
   * @param registFlg　登録フラグ(true：登録、false：更新)
   */
  updateContactStatusBgColorSetting(contactStatusBgColorSetting: IContactStatusBgColorSetting, registFlg: boolean)
    : Observable<IContactStatusBgColorSetting> {
    if (registFlg) {
      return this.contactStatusBgColorSettingResource.post<IContactStatusBgColorSetting>(contactStatusBgColorSetting);
    } else {
      return this.contactStatusBgColorSettingResource.putById<IContactStatusBgColorSetting>(
        contactStatusBgColorSetting.organizationCode,
        contactStatusBgColorSetting
      );
    }
  }


  /**
   * 組織情報を取得します。
   */
  getAllOrganizationInformation(): Observable<void> {
    // システム日付
    const field = ['organizationCode', 'organizationName'];
    const today = DateTimeUtils.today();
    const condition = [
      // 適用開始日 <= システム日付
      {
        key: 'applyStartDate',
        value: today,
        type: BlConditionType.LessThanEqual,
      },
      // 適用終了日 >= システム日付
      {
        key: 'applyEndDate',
        value: today,
        type: BlConditionType.GreaterThanEqual,
      },
      // 論理削除区分 = 有効
      {
        key: 'logicalDeleteDiv',
        value: LogicalDeleteDiv.Valid,
        type: BlConditionType.Equal,
      }
    ];
    return this.organizationInformationResource.getAll<IOrganizationInformation>({
      conditions: condition,
      fields: field,
    }, {
      consistent: false,
      entityOptions: { exPath: 'searchwithallcommonorg' }
    }).pipe(
      tap(response => {
        this.mergeOrganizationDisplayInfo(response.searchResultList || []);
        this.cacheOrgMasterItemsMap.clear();
      }),
      map(() => undefined),
      catchError(() => ObservableOf(undefined)),
    );
  }

  /**
   * 組織名称を取得します。
   * @param organizationCode　組織コード
   * @param organizationInfoList　組織情報リスト
   */
  private getOrganizationName(organizationCode: string, organizationInfoList: IOrganizationInformation[]) {
    const findInfo = _find(organizationInfoList, info => info.organizationCode === organizationCode);

    if (findInfo) {
      return findInfo.organizationName;
    } else {
      return '';
    }
  }

  /**
   * ラベルマスタ情報を取得します。
   */
  getLabel(): Observable<ILabel[]> {
    return this.labelResource.getAll<ILabel>({
      fields: ['labelCode', 'labelName', 'labelDispOrder'],
    }).pipe(
      tap(response => {
        this.LabelList = response.searchResultList;
      }),
      map(response => response.searchResultList)
    );
  }

  /**
   * 検索条件からパラメーターを作成します。
   * @param condition　検索条件
   */
  private setSearchCondition(condition: ContactSearchCondition): BlConditionInfo[] {
    const cond: BlConditionInfo[] = [];
    if (condition.contactDateSearchDiv === ContactDateSearchDiv.ContactScheduleDateTime) {
      // 実施予定日
      if (!_isEmpty(condition.contactSearchDateStart)) {
        cond.push({ key: 'contactScheduleDateTime', value: condition.contactSearchDateStart, type: BlConditionType.GreaterThanEqual });
      }
      if (!_isEmpty(condition.contactSearchDateEnd)) {
        cond.push({ key: 'contactScheduleDateTime', value: condition.contactSearchDateEnd, type: BlConditionType.LessThanEqual });
      }
    } else if (condition.contactDateSearchDiv === ContactDateSearchDiv.ContactDateTime) {
      // 実施日
      if (!_isEmpty(condition.contactSearchDateStart)) {
        cond.push({ key: 'contactDateTime', value: condition.contactSearchDateStart, type: BlConditionType.GreaterThanEqual });
      }
      if (!_isEmpty(condition.contactSearchDateEnd)) {
        cond.push({ key: 'contactDateTime', value: condition.contactSearchDateEnd, type: BlConditionType.LessThanEqual });
      }
    } else if (condition.contactDateSearchDiv === ContactDateSearchDiv.ContactStandartDate) {
      // 基準日
      if (!_isEmpty(condition.contactSearchDateStart)) {
        const startDate = DateTimeUtils.toSubdivideDateTime(condition.contactSearchDateStart).date;
        cond.push({ key: 'contactStandartDate', value: startDate, type: BlConditionType.GreaterThanEqual });
      }
      if (!_isEmpty(condition.contactSearchDateEnd)) {
        const endDate = DateTimeUtils.toSubdivideDateTime(condition.contactSearchDateEnd).date;
        cond.push({ key: 'contactStandartDate', value: endDate, type: BlConditionType.LessThanEqual });
      }
    }
    if (!_isEmpty(condition.employeeCode)) {
      cond.push({ key: 'picCode', value: condition.employeeCode, type: BlConditionType.Equal });
    }
    if (!_isEmpty(condition.contactExtractionDiv)) {
      cond.push({ key: 'contactExtractionDiv', value: condition.contactExtractionDiv, type: BlConditionType.Equal });
    }

    if (condition.recontactFlag) {
      // 再コンタクトチェックボックスにチェックが入っていた場合
      cond.push({ key: 'sysInternalLabelList', value: '再', type: BlConditionType.Match });
    }

    if (!_isEmpty(condition.customerLabelCodeList) && (condition.customerLabelCodeList.length > 0)) {
      cond.push({ key: 'customerLabelCodeList', value: condition.customerLabelCodeList, type: BlConditionType.In });
    }
    if (!_isEmpty(condition.organizationCodes) && condition.organizationCodes.length > 0) {
      cond.push({ key: 'organizationCode', value: condition.organizationCodes, type: BlConditionType.In });
    }

    // 失注理由・区分の In（単一組織選択なら組織部分を除去）
    const lostReasonIn = this.toContactListDispOrgMasterInValues(condition.contactLostReasonCode, condition.organizationCodes);
    if (!_isEmpty(lostReasonIn)) {
      cond.push({ key: 'contactLostReasonCode', value: lostReasonIn, type: BlConditionType.In });
    }
    const contactItemIn = this.toContactListDispOrgMasterInValues(condition.contactItemCode, condition.organizationCodes);
    if (!_isEmpty(contactItemIn)) {
      cond.push({ key: 'contactItemCode', value: contactItemIn, type: BlConditionType.In });
    }
    if (!_isEmpty(condition.contactDivSettingCodeList)) {
      cond.push({ key: 'contactMethodCode', value: condition.contactDivSettingCodeList, type: BlConditionType.In });
    }
    if (!_isEmpty(condition.customerNameKana1)) {
      cond.push({ key: 'customerNameKana1', value: condition.customerNameKana1, type: BlConditionType.Match });
    }
    if (!_isEmpty(condition.contactExecuteItemCodes) && (condition.contactExecuteItemCodes.length > 0)) {
      cond.push({ key: 'contactExecuteItemCode', value: condition.contactExecuteItemCodes, type: BlConditionType.In });
    }

    // 受託リース車両管理サービスが無効の場合、リース関連のコンタクト抽出項目を除外
    if (!this._isAvailableEntrustedLease) {
      cond.push({ key: 'contactExecuteItemCode', value: LEASE_CONTACT_EXECUTE_ITEM_CODE_LIST, type: BlConditionType.NotIn });
    }

    if (!_isEmpty(condition.contactExecuteResultCodes) && (condition.contactExecuteResultCodes.length > 0)) {
      cond.push({ key: 'contactExecuteResultCode', value: condition.contactExecuteResultCodes, type: BlConditionType.In });
    }
    if (!_isEmpty(condition.scheduleEventIdList)) {
      cond.push({ key: 'receptionEventID', value: condition.scheduleEventIdList, type: BlConditionType.In });
    }
    if (condition.contactExtractMPExistDiv !== ContactExtractTargetMPExistDiv.None) {
      cond.push({ key: 'contactExtractMPExistDiv', value: condition.contactExtractMPExistDiv, type: BlConditionType.Equal });
    }
    if (condition.entrustedContractExistDivSearch === ENTRUSTED_CONTRACT_EXIST_DIV_SEARCH.LEASE_ONLY) {
      cond.push({ key: 'contactList.taskInfo.entrustedContractExistDiv', value: CalculateDiv.Present, type: BlConditionType.Equal });
    } else if (condition.entrustedContractExistDivSearch === ENTRUSTED_CONTRACT_EXIST_DIV_SEARCH.BASIC_ONLY) {
      cond.push({ key: 'contactList.taskInfo.entrustedContractExistDiv', value: CalculateDiv.Absent, type: BlConditionType.Equal });
    }
    return cond;
  }

  /**
   * 汎用印刷指示情報とコンタクト一覧検索条件をコンタクト一覧印刷指示に設定して返却します。
   * @param printInstruction 汎用印刷指示情報
   * @param condition コンタクト一覧検索条件
   */
  private createPrintInstruction(
    printInstruction: IGenericPrintInstruction, condition: ContactSearchCondition): IContactListPrintInstruction {
    return {
      clientSessionId: this.clientSessionId,
      pageId: printInstruction.pageId,
      printerId: printInstruction.printerId,
      printSetNumber: printInstruction.printSetNumber,
      reportPreviewExistDiv: printInstruction.reportPreviewExistDiv,
      reportOutDiv: printInstruction.reportOutDiv,
      printPositionSetting: printInstruction.printPositionSetting,
      searchStartDateTime: condition.contactSearchDateStart,
      searchEndDateTime: condition.contactSearchDateEnd,
      picCode: condition.employeeCode,
      contactExecuteItemCodeList: condition.contactExecuteItemCodes,
      customerLabelCodeList: condition.customerLabelCodeList,
      organizationCodeList: condition.organizationCodes,
      // 印刷 API は数値コードリストのみ受け付ける。一覧用の組織付き In からコード部分を抽出する
      contactLostReasonCodeList: !_isEmpty(condition.contactLostReasonCode)
        ? _uniq(_map(condition.contactLostReasonCode, value => {
          const sepIndex = value.lastIndexOf(ORG_CODE_PAIR_SEPARATOR);
          return Number(sepIndex >= 0 ? value.substring(sepIndex + 1) : value);
        }))
        : undefined,
      contactItemCodeList: !_isEmpty(condition.contactItemCode)
        ? _uniq(_map(condition.contactItemCode, value => {
          const sepIndex = value.lastIndexOf(ORG_CODE_PAIR_SEPARATOR);
          return Number(sepIndex >= 0 ? value.substring(sepIndex + 1) : value);
        }))
        : undefined,
      customerNameKana1: condition.customerNameKana1,
    };
  }

  /**
   * グリッド表示用データを生成します。
   * @param contact コンタクト一覧表示情報
   * @param tempId 仮ID
   * @returns グリッド表示用データ
   */
  convertData(contact: IContact, tempId: number): IContactForGrid {
    // エンティティにPKが無いので独自に仮IDを振る
    const forGrid: IContactForGrid = { tempId };

    forGrid.contact = _cloneDeep(contact);
    // 下書きの「選択状態」selectStatus をチェック表示に反映する
    const contactDraft = contact as IContact & { selectStatus?: boolean | null };
    forGrid.checkParam = { checked: contactDraft.selectStatus === true, value: tempId - 1 };

    let gridData: ITaskInfo | IContactHistory;

    if (!_isEmpty(contact.taskInfo)) {
      gridData = contact.taskInfo;

      // 済（コンタクト済み）
      forGrid.implementedFlag = false;
      // 実施内容
      forGrid.executeItem = contact.taskInfo.executeOutline;
      // 実施担当
      forGrid.contactEmployeeName = contact.taskInfo.employeeName;
      // メモ
      forGrid.contactComment = contact.taskInfo.contactComment;
      // 車両管理番号
      forGrid.vehicleMgtNumber = 0;
      if (!_isEmpty(contact.taskInfo.subjects) && !_isEmpty(contact.taskInfo.subjects.sf)) {
        forGrid.vehicleMgtNumber = contact.taskInfo.subjects.sf.vehicleMgtNumber || 0;
      } else if (!_isEmpty(contact.taskInfo.subjects) && !_isEmpty(contact.taskInfo.subjects.repair)) {
        forGrid.vehicleMgtNumber = contact.taskInfo.subjects.repair.vehicleMgtNumber || 0;
      }
    } else if (!_isEmpty(contact.contactHistory)) {
      gridData = contact.contactHistory;

      // 済（コンタクト済み）
      forGrid.implementedFlag = true;
      // 実施内容
      forGrid.executeItem = contact.contactHistory.contactItemDetail;
      // 実施担当
      forGrid.contactEmployeeName = contact.contactHistory.contactEmployeeName;
      // 結果区分
      forGrid.executeResultDivName = contact.contactHistory.contactExecuteResultDivEnumName;
      if (!_isEmpty(contact.contactHistory.contactDateTime)) {
        // 実施日
        forGrid.implementDate = this.formatJapanEra(contact.contactHistory.contactDateTime.toString());
        // 時間（実施時間）
        forGrid.implementTime = contact.contactHistory.contactDateTime.toString();
      }
      // 実施結果
      forGrid.executeResult = contact.contactHistory.contactExecuteResult;
      // 失注理由
      forGrid.lostReason = contact.contactHistory.contactLostReason;
      // メモ
      forGrid.contactComment = contact.contactHistory.contactComment;
      // 車両管理番号
      forGrid.vehicleMgtNumber = contact.contactHistory.vehicleMgtNumber || 0;
    }

    // 再（再コンタクト）
    forGrid.reContactFlag = gridData.sysInternalLabelList.includes('再');
    // 拠点
    forGrid.baseName = this.getOrganizationName(gridData.organizationCode, this.organizationInfoList);
    // コンタクト区分名称
    forGrid.contactItemName = gridData.contactItemName;
    // 時間（実施予定時間）
    forGrid.scheduleTime = '';
    if (!_isEmpty(gridData.contactScheduleDateTime)) {
      // サービスカラー
      forGrid.itemServiceColor = contact.itemServiceColor;
      // 実施予定日
      forGrid.scheduleDate = this.formatJapanEra(gridData.contactScheduleDateTime.toString());
      if (gridData.contactScheduleTimeExistFlag) {
        forGrid.scheduleTime = moment(gridData.contactScheduleDateTime).format(DateTimeUtils.formatString.shotTime);
      }
    }
    // 基準日
    if (!_isEmpty(gridData.contactStandartDate)) {
      forGrid.contactStandartDate = this.formatJapanEra(gridData.contactStandartDate.toString());
    }
    // 実施手段
    forGrid.contactMethodName = gridData.contactMethodName;
    // 顧客名
    forGrid.customerName = StringUtils.concatWithSpace(gridData.customerName1, gridData.customerName2);
    // 顧客名カナ
    forGrid.customerNameKana = gridData.customerNameDisplayKana;
    // 顧客コード
    forGrid.customerCode = gridData.customerCode;
    // 顧客担当名称
    forGrid.managerName = gridData.receptPicName;
    // 連絡可能時間帯
    forGrid.contactEnableTimeName = (gridData.contactableTimeDivNameList || []).join('/');
    // 連絡種別コード
    forGrid.contactTypeCode = gridData.contactTypeCode;
    // 個人・法人区分
    forGrid.privCorpDivEnumName = gridData.privCorpDivEnumName;
    // 連絡先
    forGrid.contactInformation = gridData.contactInformation;

    if (!_isEmpty(gridData.subjects) && !_isEmpty(gridData.subjects.sf)) {
      // 車種
      forGrid.vehicleModelFullWName = gridData.subjects.sf.vehicleModelFullWName;
      // 登録番号
      forGrid.displayNumPlate = gridData.subjects.sf.displayNumPlate;
    }

    forGrid.vcleAddInfo1 = '';
    forGrid.vcleAddInfo2 = '';
    forGrid.vcleAddInfo3 = '';
    forGrid.firstRegistYearMonth = '';
    forGrid.vcleInspCertifRemarksMemo = '';
    forGrid.entrustedContractTermEndYearMonth = '';
    forGrid.vcleInspDueDate = '';
    forGrid.customerVehicleRemarksContent1 = '';
    forGrid.customerVehicleRemarksContent2 = '';
    forGrid.customerVehicleRemarksContent3 = '';
    forGrid.customerVehicleRemarksContent4 = '';
    forGrid.customerVehicleRemarksContent5 = '';
    forGrid.customerVehicleRemarksContent6 = '';
    forGrid.customerVehicleRemarksContent7 = '';
    forGrid.customerVehicleRemarksContent8 = '';
    forGrid.customerVehicleRemarksContent9 = '';
    forGrid.customerVehicleRemarksContent10 = '';
    const subj = gridData.subjects;
    const repairOrSf = !_isEmpty(subj) ? (subj.sf || subj.repair || null) : null;
    if (repairOrSf) {
      forGrid.vcleAddInfo1 = this.getVcleAddInfo1DispName(repairOrSf.vcleAddInfo1);
      forGrid.vcleAddInfo2 = repairOrSf.vcleAddInfo2 || '';
      forGrid.vcleAddInfo3 = repairOrSf.vcleAddInfo3 || '';
      forGrid.customerVehicleRemarksContent1 = repairOrSf.customerVehicleRemarksContent1;
      forGrid.customerVehicleRemarksContent2 = repairOrSf.customerVehicleRemarksContent2;
      forGrid.customerVehicleRemarksContent3 = repairOrSf.customerVehicleRemarksContent3;
      forGrid.customerVehicleRemarksContent4 = repairOrSf.customerVehicleRemarksContent4;
      forGrid.customerVehicleRemarksContent5 = repairOrSf.customerVehicleRemarksContent5;
      forGrid.customerVehicleRemarksContent6 = repairOrSf.customerVehicleRemarksContent6;
      forGrid.customerVehicleRemarksContent7 = repairOrSf.customerVehicleRemarksContent7;
      forGrid.customerVehicleRemarksContent8 = repairOrSf.customerVehicleRemarksContent8;
      forGrid.customerVehicleRemarksContent9 = repairOrSf.customerVehicleRemarksContent9;
      forGrid.customerVehicleRemarksContent10 = repairOrSf.customerVehicleRemarksContent10;
      const firstRegistYearMonth = repairOrSf.firstRegistYearMonth;
      if (!_isNil(firstRegistYearMonth) && firstRegistYearMonth !== 0) {
        forGrid.firstRegistYearMonth =
          VehicleInspCertifInfo.dispFirstRegistYearMonth(firstRegistYearMonth);
      } else {
          forGrid.firstRegistYearMonth = '';
      }
      forGrid.vcleInspCertifRemarksMemo = repairOrSf.vcleInspCertifRemarksMemo || '';
      forGrid.entrustedContractTermEndYearMonth = this.formatContactListYearMonth(
        repairOrSf.entrustedContractTermEndDate);
      const vcleInspDueDate = repairOrSf.vcleInspDueDate;
      forGrid.vcleInspDueDate = (!_isNil(vcleInspDueDate) && !_isEmpty(vcleInspDueDate)
        && !this.isBlDefaultDateString(vcleInspDueDate))
        ? this.formatJapanEra(vcleInspDueDate) : '';
    }

    // ランクラベル名称設定
    forGrid.customerRankName = this.getCustomerRankName(gridData.customerLabelCodeList);

    // ランク以外のラベルコードリスト抽出
    const customerLabelCodeList: number[] = _reject(gridData.customerLabelCodeList, (code) => {
      return CustomerConst.LABEL_CODE_CUSTOMER_RANK_START_CODE <= code && code <= CustomerConst.LABEL_CODE_CUSTOMER_RANK_END_CODE;
    });

    // ランク以外のラベル名称設定
    if (customerLabelCodeList.length !== 0) {
      // グリッド表示に必要なラベルマスタ情報のみを抽出
      const extractedLabelList: ILabel[] = [];
      _forEach(customerLabelCodeList, (customerLabelCode) => {
        _forEach(this.LabelList, (Label) => {
          if (customerLabelCode === Label.labelCode) {
            extractedLabelList.push(Label);
          }
        });
      });
      // ラベル名称結合用にラベル情報を表示順位で昇順ソート
      const sortedLabelList = _orderBy(extractedLabelList, ['labelDispOrder', 'asc']);

      // 表示順が最後のラベル以外に'／ 'を結合したラベル名称を作成
      const customerLabelNameJoined: string[] = [];
      const listIndex = sortedLabelList.length - 1;
      _forEach(sortedLabelList, (sortedLabel) => {
        let customerLabelName: string;
        if (sortedLabelList.indexOf(sortedLabel) === listIndex) {
          customerLabelName = sortedLabel.labelName;
        } else {
          customerLabelName = sortedLabel.labelName + '／ ';
        }
        customerLabelNameJoined.push(customerLabelName);
      });
      forGrid.customerLabel = customerLabelNameJoined.join('');
    }

    return forGrid;
  }

  /**
   * コンタクト一覧向け：日付を和暦年月表示に整形します。
   * @param isoDate ISO8601 日付文字列
   */
  private formatContactListYearMonth(isoDate: string | null | undefined): string {
    if (_isNil(isoDate) || _isEmpty(isoDate) || this.isBlDefaultDateString(isoDate)) {
      return '';
    }
    return DateTimeUtils.format(
      isoDate,
      DateTimeOutput.YM,
      DateTimePadding.ZERO,
      DateSeparator.JP,
      DateEra.JP
    );
  }

  /** BL初期日付（未設定相当）かどうか */
  private isBlDefaultDateString(isoDate: string): boolean {
    return isoDate === '1900-01-01' || isoDate.indexOf('1900-01-01') === 0;
  }

  /**
   * 自動車の追加情報1の表示文字列を取得します（区分マスタ VcleAdditionalInfoCd で名称解決）。
   * @param code コード
   * @returns 区分名称（未解決時、コードなしは空文字）
   */
  private getVcleAddInfo1DispName(code: number | string): string {
    if (_isNil(code) || _toString(code) === '') {
      return '';
    }
    const list = this.vcleAdditionalInfoCdList;
    if (_isNil(list) || _isEmpty(list)) {
      return '';
    }
    const item = _find(list, s => _toString(s.code) === _toString(code));
    return !_isNil(item) ? item.name : '';
  }

  /**
   * 顧客ランク名称を取得します。
   * @param customerLabelCodeList 顧客ラベルコードリスト
   * @returns 顧客ランク名称
   */
  private getCustomerRankName(customerLabelCodeList: number[]): string {
    // 顧客ランクを示すラベル文字列を抽出
    const customerRankLabelNameList = _compact(_map(customerLabelCodeList, customerLabelCode => {
      const isCustomerRank = customerLabelCode >= CustomerConst.LABEL_CODE_CUSTOMER_RANK_START_CODE
        && customerLabelCode <= CustomerConst.LABEL_CODE_CUSTOMER_RANK_END_CODE;
      let label: ILabel;
      if (isCustomerRank) {
        label = _find(this.LabelList, customerLabel => customerLabel.labelCode === customerLabelCode);
      }
      return !_isNil(label) ? _trim(label.labelName) : null;
    }));

    // NOTE: ランクを示すラベルは1つしか持っていないハズだが念のため
    return customerRankLabelNameList.join('/');
  }

  /**
   * Moment形式の年月日時を和暦文字列に変換します。
   * @param params　グリッド設定値
   */
  private formatJapanEra(params: string): string {
    const japanEraDate = DateTimeUtils.formatJapanEra(DateTimeUtils.parseDate(params));

    moment.locale('ja');
    const weekDay = moment(params).format('dd');

    return japanEraDate + '(' + weekDay + ')';
  }

  /**
   * 一括更新処理を開始します。
   * @param bulkEditDiv 一括編集区分
   * @param viewItemList グリッド描画中リスト
   */
  startBulkEdit(contactMultiplePartialUpdate: IContactMultiplePartialUpdate): Observable<null> {
    // 一括更新リクエスト送信
    return this.connectWebSocketIfNeeded().pipe(
      mergeMap(
        isConnect => {
          if (isConnect) {
            return this.updateContact(contactMultiplePartialUpdate);
          } else {
            return RxOf(null);
          }
        }
      )
    );
  }

  /**
   * コンタクト情報一括削除処理を開始します。
   * @param contactMultipleDelete コンタクト情報一括削除
   */
  startBulkDelete(contactMultipleDelete: IContactMultipleDelete): Observable<null> {
    // 一括更新リクエスト送信
    return this.connectWebSocketIfNeeded().pipe(
      mergeMap(
        isConnect => {
          if (isConnect) {
            return this.deleteContact(contactMultipleDelete);
          } else {
            return RxOf(null);
          }
        }
      )
    );
  }

  /**
   * 必要であれば一括更新進捗状況の受信用WebSocketに接続します。
   * @returns 接続結果
   */
  connectWebSocketIfNeeded(): Observable<boolean> {
    // 接続済なら何もしない
    if (!_isNil(this.webSocketSubject)) {
      return RxOf(true);
    }

    return this.getWssUrl().pipe(
      mergeMap(
        wssUrl => {
          this.webSocketSubject = new WebsocketSubject(wssUrl, 0, 0);
          return this.webSocketSubject.connectionStatus;
        }
      ),
      tap(
        isConnect => {
          if (isConnect) {
            this.webSocketSubject.subscribe(
              response => {
                if (!_isNil(response) && !_isNil(response.message)) {
                  // メッセージ内のJSON文字列をオブジェクトにパース
                  try {
                    const progress: IBulkUpdateProgress = JSON.parse(response.message);
                    if (!_isEmpty(progress)) {
                      this.bulkUpdateProgressSubject.next(progress);
                    }
                  } catch (error) {
                    console.error(error);
                  }
                }
              }
            );
          } else {
            // WebSocket接続失敗(※ローカル起動は失敗します)
            this.webSocketSubject.unsubscribe();
            this.webSocketSubject = null;

            // エラー送信
            this.bulkUpdateProgressSubject.next({
              bulkUpdateStatus: BulkUpdateStatus.Error,
              messageContents: this.ERR_NETWORK
            });
          }
        }
      )
    );
  }

  /**
   * 一括更新進捗状況の受信用WebSocketURLを取得します。
   * @returns URL
   */
  protected getWssUrl(): Observable<string> {
    // ドメインのベースURLを取得
    return this.blApiLocator.getBaseUrl(this.WEB_SOCKET.DOMAIN, false).pipe(
      map(
        baseUrl => {
          // プロトコルを変更してエンドポイント追加
          const httpsUrl = _startsWith(baseUrl, location.protocol) ? baseUrl : `${location.origin}${baseUrl}`;
          let wssUrl = `${_replace(httpsUrl, location.protocol, this.WEB_SOCKET.PROTOCOL)}`;
          wssUrl += `${this.WEB_SOCKET.ENDPOINT}/ws`;

          // パラメータを付与して返却
          wssUrl += `?token=${CookieUtils.get(Const.Cookie.Key.ID_TOKEN)}&unique-id=${this.bulkUpdateWebSocketId}`;
          return wssUrl;
        }
      )
    );
  }


  /**
   * 編集処理中のメッセージを取得します。
   * @param execCnt 処理件数
   * @param totalCnt 総件数
   * @returns 編集処理中メッセージ
   */
  getEditingMessage(execCnt: number, totalCnt: number): string {
    let message = this.INFO_EDITING;
    message = _replace(message, this.REPLACE.EXEC_CNT, `${execCnt}`);
    message = _replace(message, this.REPLACE.TOTAL_CNT, `${totalCnt}`);
    return message;
  }

  /**
   * 一括結果紐づけ用の主キー文字列を組み立てる（API の createBulkUpdateResult / 画面 `applyBulk` と同一規則）。
   */
  private buildRowPkForBulkResult(displayContact: IContactEx): string {
    if (!_isEmpty(displayContact.taskInfo)) {
      return displayContact.taskInfo.blTenantId
        + '_'
        + displayContact.taskInfo.taskNum
        + '_'
        + displayContact.taskInfo.disperseTaskNum;
    }
    const contactOriginTable = !_isNil(displayContact.contactHistory.contactOriginTable)
      ? displayContact.contactHistory.contactOriginTable
      : '';
    const contactOriginTableKey = !_isNil(displayContact.contactHistory.contactOriginTableKey)
      ? displayContact.contactHistory.contactOriginTableKey
      : '';
    const contactOriginSubKey = !_isNil(displayContact.contactHistory.contactOriginSubKey)
      ? displayContact.contactHistory.contactOriginSubKey
      : '';
    return displayContact.contactHistory.blTenantId
      + '_'
      + displayContact.contactHistory.disperseContactCode
      + '_'
      + contactOriginTable
      + '_'
      + contactOriginTableKey
      + '_'
      + contactOriginSubKey
      + '_'
      + displayContact.contactHistory.contactOriginSubKeyDiv
      + '_'
      + displayContact.contactHistory.contactHistoryBranchNum
      + '_'
      + displayContact.contactHistory.customerCode;
  }

  /**
   * 一括更新結果をメモリにキャッシュします。
   * @param bulk 一括更新結果リスト
   */
  private mergeBulkProcessResultsIntoCache(bulk: IBulkProcessResult[]): void {
    _forEach(bulk, (r) => {
      if (!_isNil(r) && !_isEmpty(r.bulkProcessTargetKey)) {
        this.bulkUpdateResultStatusByTargetKey.set(
          r.bulkProcessTargetKey,
          r.bulkProcessResultDivEnumName != null ? _toString(r.bulkProcessResultDivEnumName) : ''
        );
      }
    });
  }

  /**
   * 一括更新結果を適用します。
   * 現在ページの下書きのみ再取得ではサーバ反映前の古い内容になり得るため、
   * {@link getContactListDisp}（一覧再抽出・進捗ポーリング・下書き取得）で最新の一覧を取得してから一括結果をマージします。
   * @param condition コンタクト一覧検索条件
   */
  applyBulkUpdateResult(condition: ContactSearchCondition): Observable<IContactEx[]> {
    let resDisplayContacts: IContactEx[] = [];
    let wkDisplayContacts: IContactEx[] = [];
    return ObservableForkJoin(
      this.searchBulkUpdateResultList(),
      this.getContactListDisp(condition),
    ).pipe(
      map(
        ([bulkProcessResults, displayContacts]) => {
          wkDisplayContacts = displayContacts;
          this.mergeBulkProcessResultsIntoCache(bulkProcessResults);
          _forEach(wkDisplayContacts, displayContact => {
            const pk = this.buildRowPkForBulkResult(displayContact);
            displayContact.gridData.updateStatus = _defaultTo(this.bulkUpdateResultStatusByTargetKey.get(pk), '');
          });
          // 更新状況が、処理失敗、処理成功、ブランクの順に並び替える
          resDisplayContacts = _filter(
            wkDisplayContacts,
            (item) => item.gridData.updateStatus === this.BULK_PRO_RES_DIV_ENUM_JAP_NAME.FAILURE
          );
          const resDisplayContactsSuccess: IContactEx[] = _filter(
            wkDisplayContacts,
            (item) => item.gridData.updateStatus === this.BULK_PRO_RES_DIV_ENUM_JAP_NAME.SUCCESS
          );
          const resDisplayContactsBlank: IContactEx[] = _filter(wkDisplayContacts, (item) => item.gridData.updateStatus === '');
          return _concat(resDisplayContacts, resDisplayContactsSuccess, resDisplayContactsBlank);
        }
      ), finalize(() => {
        // 一括更新結果を削除
        this.deleteBulkUpdateResult().subscribe(
          // お掃除的な削除なので失敗しても特に何もしない
          () => { },
          _e => { }
        );
      })
    );
  }

  /**
   * 一括更新結果を検索して返します。
   * @returns 一括更新結果リスト
   */
  searchBulkUpdateResultList(): Observable<IBulkProcessResult[]> {
    const conditions = [
      { key: 'bulkUpdateManageId', value: this.bulkUpdateManageId, type: BlConditionType.Equal }
    ];
    return this.bulkProcessResultResource.getAll<IBulkProcessResult>({ conditions }).pipe(
      map(
        response => {
          return response.searchResultList;
        }
      )
    );
  }

  /**
   * 一括更新結果を削除します。
   * @returns 完了通知
   */
  deleteBulkUpdateResult(): Observable<null> {
    const requestBody: IBulkProcessResult = { bulkUpdateManageId: this.bulkUpdateManageId };
    return this.bulkProcessResultResource.post(requestBody, { entityOptions: { exPath: 'deleteforbulkordermanageid' } }).pipe(
      map(() => null),
      finalize(() => {
        // ここまで完了したら一括更新管理IDはクリア
        this.bulkUpdateManageId = null;
      })
    );
  }

  /**
   * 一括更新進捗状況
   */
  get bulkUpdateProgress$(): Observable<IBulkUpdateProgress> {
    return this.bulkUpdateProgressSubject.asObservable();
  }

  /**
   * テキスト出力が利用可能であるか判定します。
   * @returns 判定結果
   */
  isAvailableTextOutPut(): Observable<boolean> {
    return ObservableForkJoin(
      this.loginResourceService.isAvailableService(ContractConst.SERVICEID_OPT_CMN_DATA_OUTPUT),
      this.loginResourceService.isAvailableFunction(RoleConst.ROLEID_MENU_ALL_TEXT_OUTPUT)
    ).pipe(
      map(
        responseList => {
          return _every(responseList, Boolean);
        }
      )
    );
  }

  /** リース情報ラジオ用にコンタクト抽出対象リース受託契約有無区分を正規化します
   *
   * @param extractDiv コンタクト抽出対象リース受託契約有無区分
   * @param defaultWhenUnset 未設定時のデフォルト値
   * @returns 正規化されたコンタクト抽出対象リース受託契約有無区分
   */
  normalizeContactExtractEntrustedContractExistDivForRadio(
    extractDiv: string | null | undefined,
    defaultWhenUnset: string = ContactExtractTargetEntrustedContractExistDiv.All,
  ): string {
    const trimmed = extractDiv == null || extractDiv === '' ? '' : String(extractDiv).trim();
    if (trimmed === ContactExtractTargetEntrustedContractExistDiv.All
      || trimmed === ContactExtractTargetEntrustedContractExistDiv.BasicOnly
      || trimmed === ContactExtractTargetEntrustedContractExistDiv.LeaseOnly) {
      return trimmed;
    }
    if (trimmed !== '' && trimmed !== ContactExtractTargetEntrustedContractExistDiv.None) {
      const mapped = this.mapEntrustedContractExistDivSearchToContactExtract(trimmed);
      if (mapped !== ContactExtractTargetEntrustedContractExistDiv.None) {
        return mapped;
      }
    }
    return defaultWhenUnset;
  }

  /** コンタクト抽出対象リース受託契約有無区分を一覧検索条件用のコードに変換します
   *
   * @param div コンタクト抽出対象リース受託契約有無区分
   * @returns 一覧検索条件用のコード
   */
  private mapContactExtractEntrustedContractExistDivToSearch(div: string | null | undefined): string {
    switch (div) {
      case ContactExtractTargetEntrustedContractExistDiv.All:
        return ENTRUSTED_CONTRACT_EXIST_DIV_SEARCH.ALL;
      case ContactExtractTargetEntrustedContractExistDiv.LeaseOnly:
        return ENTRUSTED_CONTRACT_EXIST_DIV_SEARCH.LEASE_ONLY;
      case ContactExtractTargetEntrustedContractExistDiv.BasicOnly:
        return ENTRUSTED_CONTRACT_EXIST_DIV_SEARCH.BASIC_ONLY;
      default:
        return ENTRUSTED_CONTRACT_EXIST_DIV_SEARCH.NONE;
    }
  }

  /** 一覧検索条件用のコードをコンタクト抽出対象リース受託契約有無区分に変換します
   *
   * @param div 一覧検索条件用のコード
   * @returns コンタクト抽出対象リース受託契約有無区分
   */
  private mapEntrustedContractExistDivSearchToContactExtract(div: string | null | undefined): string {
    switch (div) {
      case ENTRUSTED_CONTRACT_EXIST_DIV_SEARCH.ALL:
        return ContactExtractTargetEntrustedContractExistDiv.All;
      case ENTRUSTED_CONTRACT_EXIST_DIV_SEARCH.LEASE_ONLY:
        return ContactExtractTargetEntrustedContractExistDiv.LeaseOnly;
      case ENTRUSTED_CONTRACT_EXIST_DIV_SEARCH.BASIC_ONLY:
        return ContactExtractTargetEntrustedContractExistDiv.BasicOnly;
      default:
        return ContactExtractTargetEntrustedContractExistDiv.None;
    }
  }

  /** コンタクト抽出対象DM案内区分を一覧検索条件用のコードに変換します
   *
   * @param div コンタクト抽出対象DM案内区分
   * @returns 一覧検索条件用のコード
   */
  private mapContactExtractDmSendDivToSearchDmSendDiv(div: string | null | undefined): string {
    switch (div) {
      case ContactExtractDmSendDiv.Send:
        return DmSendDiv.Send;
      case ContactExtractDmSendDiv.NoSend:
        return DmSendDiv.NoSend;
      case ContactExtractDmSendDiv.All:
      default:
        return '';
    }
  }

  /** コンタクト抽出対象リース受託契約有無区分を一覧検索条件用のコードに変換します
   *
   * @param value コンタクト抽出対象リース受託契約有無区分
   * @param defaultCode 未設定時のデフォルト値
   * @returns 正規化されたコンタクト抽出対象リース受託契約有無区分
   */
  private normalizeEntrustedContractExistDivSearchForListRadio(
    value: string | null | undefined,
    defaultCode: string = ENTRUSTED_CONTRACT_EXIST_DIV_SEARCH.ALL,
  ): string {
    const v = value == null || value === '' ? '' : String(value).trim();
    if (v === ENTRUSTED_CONTRACT_EXIST_DIV_SEARCH.LEASE_ONLY
      || v === ENTRUSTED_CONTRACT_EXIST_DIV_SEARCH.BASIC_ONLY
      || v === ENTRUSTED_CONTRACT_EXIST_DIV_SEARCH.ALL) {
      return v;
    }
    return defaultCode;
  }
}



// WEBPACK FOOTER //
// ./src/app/page/contact-list-page/contact-list-page.service.ts